import { useState, useRef, useEffect } from "react";
import BackOffice from './BackOffice';
import Boutique from './Boutique';
import EspaceClient from './EspaceClient';

// ══════════════════════════════════════════════
//  DONNÉES CMS — lit depuis localStorage
// ══════════════════════════════════════════════

const DEFAULT_DATA = {
  accueil: {
    tag: "Aide administrative & Secrétariat — Guadeloupe 971",
    titre: "Votre assistante administrative de confiance",
    description: "12 ans d'expérience au service de votre entreprise en Guadeloupe. Aide administrative, déclaration d'impôts, création d'entreprise, secrétariat à distance — je gère votre administratif.",
    stat1_valeur: "12+", stat1_label: "Ans d'expérience",
    stat2_valeur: "100%", stat2_label: "Confidentialité",
    stat3_valeur: "48h", stat3_label: "Délai de réponse",
    bouton1: "Prendre rendez-vous",
    bouton2: "Générer un document IA ✦",
  },
  apropos: {
    titre: "Cordinel Daniela",
    paragraphe1: "Après 12 ans passés en tant que responsable de la planification et de la gestion des stocks, secrétaire polyvalente et responsable administrative et comptable, j'ai décidé de créer mon cabinet de consulting.",
    paragraphe2: "Spécialisée dans le domaine administratif et dans le génie climatique, énergie, environnement et développement durable.",
    paragraphe3: "J'ai également effectué la formation de formateur occasionnel permettant de fournir des formations de qualité dans des domaines divers et variés.",
    citation: "Je m'engage à vous offrir la fiabilité, l'efficacité, la discrétion, une intervention rapide et soignée.",
    poste: "Fondatrice — CD'ASSIST CONSULTING",
    localisation: "Guadeloupe, France",
  },
  prestations: [
    { id: 1, icon: "🏢", titre: "Création d'entreprise", description: "Statuts, immatriculation, Kbis — toutes les démarches de A à Z. Accompagnement personnalisé de la création à l'immatriculation.", prix: "Sur devis", lien: "boutique" },
    { id: 2, icon: "📋", titre: "Secrétariat administratif", description: "Rédaction de courriers, gestion des mails, classement, archivage. Disponible au cabinet ou à distance.", prix: "25€/h", lien: "boutique" },
    { id: 3, icon: "🧾", titre: "Déclaration d'impôts", description: "Accompagnement complet pour votre déclaration fiscale annuelle. Apportez vos documents, on s'occupe du reste.", prix: "25€/h", lien: "contact" },
    { id: 4, icon: "⚖️", titre: "Rédaction de contrats", description: "CDI, CDD, contrats commerciaux, CGV, NDA — conformes au droit français. Rédaction sur mesure selon votre situation.", prix: "À partir de 150€", lien: "boutique" },
    { id: 5, icon: "📊", titre: "Comptabilité & Facturation", description: "Suivi comptable, établissement de factures, relances clients. Externalisez votre comptabilité en toute sérénité.", prix: "Sur devis", lien: "boutique" },
    { id: 6, icon: "🌱", titre: "Conseil ENR & Environnement", description: "Expertise en génie climatique, énergie renouvelable et développement durable. Spécialisée en solutions ENR pour la Guadeloupe.", prix: "Sur devis", lien: "contact" },
  ],
  tarifs: {
    forfaits: [
      { id: 1, nom: "Coup de Pouce", prix: "250", heures: "10h/mois", populaire: false },
      { id: 2, nom: "Petit Budget", prix: "400", heures: "16h/mois", populaire: false },
      { id: 3, nom: "Moyen Budget", prix: "600", heures: "26h/mois", populaire: true },
      { id: 4, nom: "Budget Confort", prix: "800", heures: "34h/mois", populaire: false },
      { id: 5, nom: "Budget Sérénité", prix: "1200", heures: "50h/mois", populaire: false },
    ],
    alacarte: [
      { id: 1, label: "Aide à la pré-comptabilité", prix: "150€/mois" },
      { id: 2, label: "Prévisionnel sur 3 ans", prix: "450€" },
      { id: 3, label: "Étude de marché", prix: "350€" },
      { id: 4, label: "Tarif à l'heure au cabinet", prix: "25€/h" },
      { id: 5, label: "Frais de déplacement", prix: "25€" },
    ],
  },
  contact: {
    telephone1: "0690 96 10 04",
    telephone2: "0690 96 50 10",
    email: "cdassist97190@gmail.com",
    adresse: "Guadeloupe, France",
    horaires: "Lun–Ven · 9h–18h",
    site: "www.cd-assist.com",
  },
  theme: { couleur1: "#C4006A", couleur2: "#6B2D8B" },
};

function getCMS() {
  try {
    const s = JSON.parse(localStorage.getItem("cdassist_cms") || "{}");
    return {
      accueil: { ...DEFAULT_DATA.accueil, ...s.accueil },
      apropos: { ...DEFAULT_DATA.apropos, ...s.apropos },
      prestations: s.prestations || DEFAULT_DATA.prestations,
      tarifs: { forfaits: s.tarifs?.forfaits || DEFAULT_DATA.tarifs.forfaits, alacarte: s.tarifs?.alacarte || DEFAULT_DATA.tarifs.alacarte },
      contact: { ...DEFAULT_DATA.contact, ...s.contact },
      theme: { ...DEFAULT_DATA.theme, ...s.theme },
    };
  } catch { return DEFAULT_DATA; }
}

function Logo({ size = 48 }) {
  return <img src="/LOGO_CD_ASSIST.png" alt="CD'ASSIST CONSULTING" style={{ width: size, height: size, objectFit: "contain" }} />;
}

const DOCS = [
  { id: "sas", icon: "⚖️", label: "Statuts SAS", fields: [
    { key: "company_name", label: "Nom de la société", placeholder: "Ex: TechVision SAS" },
    { key: "capital", label: "Capital social (€)", placeholder: "Ex: 10 000" },
    { key: "siege", label: "Siège social", placeholder: "Ex: Pointe-à-Pitre, Guadeloupe" },
    { key: "president", label: "Nom du Président", placeholder: "Ex: Jean Dupont" },
    { key: "objet", label: "Objet social", placeholder: "Ex: Développement de logiciels" },
  ]},
  { id: "sarl", icon: "🏛️", label: "Statuts SARL", fields: [
    { key: "company_name", label: "Nom de la société", placeholder: "Ex: Dupont SARL" },
    { key: "capital", label: "Capital social (€)", placeholder: "Ex: 5 000" },
    { key: "siege", label: "Siège social", placeholder: "Ex: Basse-Terre, Guadeloupe" },
    { key: "gerant", label: "Nom du Gérant", placeholder: "Ex: Marie Martin" },
    { key: "objet", label: "Objet social", placeholder: "Ex: Commerce général" },
  ]},
  { id: "cdi", icon: "📄", label: "Contrat CDI", fields: [
    { key: "company_name", label: "Nom de l'entreprise", placeholder: "Ex: CD'ASSIST CONSULTING" },
    { key: "employee_name", label: "Nom du salarié", placeholder: "Ex: Sophie Bernard" },
    { key: "poste", label: "Intitulé du poste", placeholder: "Ex: Assistante administrative" },
    { key: "salaire", label: "Salaire brut mensuel (€)", placeholder: "Ex: 2 200" },
    { key: "date_debut", label: "Date de début", placeholder: "Ex: 01/06/2025" },
  ]},
  { id: "cgv", icon: "🛒", label: "CGV", fields: [
    { key: "company_name", label: "Nom de l'entreprise", placeholder: "Ex: CD'ASSIST CONSULTING" },
    { key: "activite", label: "Type d'activité", placeholder: "Ex: Secrétariat externalisé" },
    { key: "siege", label: "Siège social", placeholder: "Ex: Guadeloupe" },
    { key: "siren", label: "Numéro SIREN", placeholder: "Ex: 123 456 789" },
    { key: "email_contact", label: "Email de contact", placeholder: "Ex: contact@cd-assist.com" },
  ]},
  { id: "nda", icon: "🔐", label: "Accord de Confidentialité", fields: [
    { key: "company_name", label: "Entreprise divulgatrice", placeholder: "Ex: CD'ASSIST CONSULTING" },
    { key: "recipient", label: "Partie réceptrice", placeholder: "Ex: Jean Dupont" },
    { key: "objet", label: "Objet confidentiel", placeholder: "Ex: Données clients" },
    { key: "duree", label: "Durée (ans)", placeholder: "Ex: 3" },
  ]},
  { id: "impot", icon: "🧾", label: "Dossier Déclaration Impôts", fields: [
    { key: "nom", label: "Nom complet", placeholder: "Ex: Daniela Cordinel" },
    { key: "adresse", label: "Adresse fiscale", placeholder: "Ex: Guadeloupe" },
    { key: "situation", label: "Situation familiale", placeholder: "Ex: Célibataire, 1 enfant" },
    { key: "revenus", label: "Type de revenus", placeholder: "Ex: Salaires, revenus d'activité" },
    { key: "annee", label: "Année fiscale", placeholder: "Ex: 2024" },
  ]},
];

const AVANTAGES = [
  { icon: "🏆", title: "Professionnalisme", desc: "12 ans d'expérience, exigence de qualité et satisfaction client garantis." },
  { icon: "⏱️", title: "Gain de temps", desc: "Déléguez vos tâches et recentrez-vous sur votre cœur de métier." },
  { icon: "💰", title: "Réduction des coûts", desc: "Pas de charges sociales, pas de congés payés, pas d'investissement matériel." },
  { icon: "🤝", title: "Souplesse & Flexibilité", desc: "Mission ponctuelle ou régulière, prestations à la carte, sans engagement." },
  { icon: "🔒", title: "Confidentialité totale", desc: "Engagement contractuel de non-divulgation de vos informations." },
  { icon: "☀️", title: "Éco-responsable", desc: "Cabinet entièrement alimenté par l'énergie solaire." },
];

const SERVICES_LIST = ["Création d'entreprise","Secrétariat administratif","Déclaration d'impôts 📅","Rédaction de contrats","Comptabilité","Conseil ENR","Formation","Autre"];

const HEURES_IMPOTS = ["09:00","09:30","10:00","10:30","11:00","14:00","14:30","15:00","15:30","16:00"];

const DOCS_IMPOTS = [
  "Avis d'imposition N-1",
  "Bulletin de salaire de décembre",
  "Justificatifs de charges déductibles",
  "Relevés bancaires",
  "Documents de revenus fonciers",
  "Justificatifs de dons",
];
const JOURS_SEMAINE = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
const NAV_LINKS = [
  { id: "home", label: "Accueil" },
  { id: "apropos", label: "À propos" },
  { id: "prestations", label: "Prestations" },
  { id: "documents", label: "Documents IA" },
  { id: "outils", label: "🔧 Outils" },
  { id: "boutique", label: "🛒 Boutique" },
  { id: "tarifs", label: "Tarifs" },
  { id: "contact", label: "Contact" },
];

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=DM+Sans:wght@300;400;500;600;700&display=swap');
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:'DM Sans',sans-serif;}
  ::-webkit-scrollbar{width:5px;}
  ::-webkit-scrollbar-thumb{background:linear-gradient(#C4006A,#6B2D8B);border-radius:10px;}
  input:focus,select:focus,textarea:focus{border-color:#C4006A!important;outline:none;box-shadow:0 0 0 3px rgba(196,0,106,0.09);}
  @keyframes pulse{0%,80%,100%{opacity:.2;transform:scale(.8)}40%{opacity:1;transform:scale(1.2)}}
  @keyframes fadeUp{from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:translateY(0)}}
  @keyframes slideIn{from{opacity:0;transform:translateX(100%)}to{opacity:1;transform:translateX(0)}}
  .fade-up{animation:fadeUp .6s ease both;}
  .card-hover{transition:all .3s;}
  .card-hover:hover{transform:translateY(-6px);box-shadow:0 16px 48px rgba(107,45,139,0.16)!important;border-color:#C4006A!important;}
  .btn-primary{background:linear-gradient(135deg,#C4006A,#8B2FC9);color:#fff;border:none;padding:14px 36px;border-radius:50px;font-family:'DM Sans',sans-serif;font-weight:700;font-size:14px;cursor:pointer;transition:all .3s;}
  .btn-primary:hover{transform:translateY(-2px);box-shadow:0 8px 28px rgba(196,0,106,0.4);}
  .btn-outline{background:transparent;color:#6B2D8B;border:2px solid #6B2D8B;padding:12px 28px;border-radius:50px;font-family:'DM Sans',sans-serif;font-weight:600;font-size:14px;cursor:pointer;transition:all .3s;}
  .btn-outline:hover{background:#6B2D8B;color:#fff;}
  .tag{display:inline-block;background:linear-gradient(135deg,rgba(196,0,106,0.1),rgba(107,45,139,0.1));color:#C4006A;padding:5px 16px;border-radius:20px;font-size:11px;font-weight:700;letter-spacing:2px;text-transform:uppercase;border:1px solid rgba(196,0,106,0.2);}
  .field-label{display:block;font-size:11px;font-weight:700;color:#C4006A;letter-spacing:1.5px;text-transform:uppercase;margin-bottom:8px;}
  .field-input{width:100%;border:1.5px solid #e5d5f5;border-radius:10px;padding:12px 16px;font-family:'DM Sans',sans-serif;font-size:14px;color:#1A0A2E;background:#faf7ff;}
  .mobile-menu{animation:slideIn .3s ease;}
  @media(max-width:768px){
    .desktop-nav{display:none!important;}
    .hamburger{display:flex!important;}
    .hero-grid{grid-template-columns:1fr!important;}
    .hero-card{display:none!important;}
    .stats-row{gap:20px!important;}
    .section-grid{grid-template-columns:1fr!important;}
    .footer-grid{grid-template-columns:1fr!important;}
    .contact-grid{grid-template-columns:1fr!important;}
    .contact-info-row{grid-template-columns:1fr!important;}
    .tarifs-grid{grid-template-columns:repeat(2,1fr)!important;}
    .apropos-grid{grid-template-columns:1fr!important;}
  }
  @media(min-width:769px){
    .hamburger{display:none!important;}
    .mobile-menu-overlay{display:none!important;}
  }
`;

// ══════════════════════════════════════════════
//  COMPOSANT CODE APE
// ══════════════════════════════════════════════

function ApeSearch({ C1, C2 }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  const EXEMPLES = [
    "Secrétariat et assistance administrative à distance",
    "Conseil en gestion et création d'entreprise",
    "Commerce de détail alimentaire",
    "Développement de logiciels",
    "Travaux d'électricité et installation ENR",
  ];

  const TOUS_CODES_APE = [
  { code: "01.11Z", libelle: "Culture de céréales (à l'exception du riz), de légumineuses et de graines oléagineuses" },
  { code: "01.12Z", libelle: "Culture du riz" },
  { code: "01.13Z", libelle: "Culture de légumes, de melons, de racines et de tubercules" },
  { code: "01.14Z", libelle: "Culture de la canne à sucre" },
  { code: "01.15Z", libelle: "Culture du tabac" },
  { code: "01.16Z", libelle: "Culture de plantes à fibres" },
  { code: "01.19Z", libelle: "Autres cultures non permanentes" },
  { code: "01.21Z", libelle: "Culture de la vigne" },
  { code: "01.22Z", libelle: "Culture de fruits tropicaux et subtropicaux" },
  { code: "01.23Z", libelle: "Culture d'agrumes" },
  { code: "01.24Z", libelle: "Culture de fruits à pépins et à noyau" },
  { code: "01.25Z", libelle: "Culture d'autres fruits d'arbres ou d'arbustes et de fruits à coque" },
  { code: "01.26Z", libelle: "Culture de fruits oléagineux" },
  { code: "01.27Z", libelle: "Culture de plantes à boissons" },
  { code: "01.28Z", libelle: "Culture de plantes à épices, aromatiques, médicinales et pharmaceutiques" },
  { code: "01.29Z", libelle: "Autres cultures permanentes" },
  { code: "01.30Z", libelle: "Reproduction de plantes" },
  { code: "01.41Z", libelle: "Élevage de vaches laitières" },
  { code: "01.42Z", libelle: "Élevage d'autres bovins et de buffles" },
  { code: "01.43Z", libelle: "Élevage de chevaux et d'autres équidés" },
  { code: "01.44Z", libelle: "Élevage de chameaux et d'autres camélidés" },
  { code: "01.45Z", libelle: "Élevage d'ovins et de caprins" },
  { code: "01.46Z", libelle: "Élevage de porcins" },
  { code: "01.47Z", libelle: "Élevage de volailles" },
  { code: "01.49Z", libelle: "Élevage d'autres animaux" },
  { code: "01.50Z", libelle: "Culture et élevage associés" },
  { code: "01.61Z", libelle: "Activités de soutien aux cultures" },
  { code: "01.62Z", libelle: "Activités de soutien à la production animale" },
  { code: "01.63Z", libelle: "Traitement primaire des récoltes" },
  { code: "01.64Z", libelle: "Traitement des semences" },
  { code: "01.70Z", libelle: "Chasse, piégeage et services annexes" },
  { code: "02.10Z", libelle: "Sylviculture et autres activités forestières" },
  { code: "02.20Z", libelle: "Exploitation forestière" },
  { code: "02.30Z", libelle: "Récolte de produits forestiers non ligneux poussant à l'état sauvage" },
  { code: "02.40Z", libelle: "Services de soutien à l'exploitation forestière" },
  { code: "03.11Z", libelle: "Pêche en mer" },
  { code: "03.12Z", libelle: "Pêche en eau douce" },
  { code: "03.21Z", libelle: "Aquaculture en mer" },
  { code: "03.22Z", libelle: "Aquaculture en eau douce" },
  { code: "05.10Z", libelle: "Extraction de houille" },
  { code: "05.20Z", libelle: "Extraction de lignite" },
  { code: "06.10Z", libelle: "Extraction de pétrole brut" },
  { code: "06.20Z", libelle: "Extraction de gaz naturel" },
  { code: "07.10Z", libelle: "Extraction de minerais de fer" },
  { code: "07.21Z", libelle: "Extraction de minerais d'uranium et de thorium" },
  { code: "07.29Z", libelle: "Extraction d'autres minerais de métaux non ferreux" },
  { code: "08.11Z", libelle: "Extraction de pierres ornementales et de construction, de calcaire industriel, de gypse, de craie et d'ardoise" },
  { code: "08.12Z", libelle: "Exploitation de gravières et sablières, extraction d’argiles et de kaolin" },
  { code: "08.91Z", libelle: "Extraction des minéraux chimiques et d'engrais minéraux" },
  { code: "08.92Z", libelle: "Extraction de tourbe" },
  { code: "08.93Z", libelle: "Production de sel" },
  { code: "08.99Z", libelle: "Autres activités extractives n.c.a." },
  { code: "09.10Z", libelle: "Activités de soutien à l'extraction d'hydrocarbures" },
  { code: "09.90Z", libelle: "Activités de soutien aux autres industries extractives" },
  { code: "10.11Z", libelle: "Transformation et conservation de la viande de boucherie" },
  { code: "10.12Z", libelle: "Transformation et conservation de la viande de volaille" },
  { code: "10.13A", libelle: "Préparation industrielle de produits à base de viande" },
  { code: "10.13B", libelle: "Charcuterie" },
  { code: "10.20Z", libelle: "Transformation et conservation de poisson, de crustacés et de mollusques" },
  { code: "10.31Z", libelle: "Transformation et conservation de pommes de terre" },
  { code: "10.32Z", libelle: "Préparation de jus de fruits et légumes" },
  { code: "10.39A", libelle: "Autre transformation et conservation de légumes" },
  { code: "10.39B", libelle: "Transformation et conservation de fruits" },
  { code: "10.41A", libelle: "Fabrication d'huiles et graisses brutes" },
  { code: "10.41B", libelle: "Fabrication d'huiles et graisses raffinées" },
  { code: "10.42Z", libelle: "Fabrication de margarine et graisses comestibles similaires" },
  { code: "10.51A", libelle: "Fabrication de lait liquide et de produits frais" },
  { code: "10.51B", libelle: "Fabrication de beurre" },
  { code: "10.51C", libelle: "Fabrication de fromage" },
  { code: "10.51D", libelle: "Fabrication d'autres produits laitiers" },
  { code: "10.52Z", libelle: "Fabrication de glaces et sorbets" },
  { code: "10.61A", libelle: "Meunerie" },
  { code: "10.61B", libelle: "Autres activités du travail des grains" },
  { code: "10.62Z", libelle: "Fabrication de produits amylacés" },
  { code: "10.71A", libelle: "Fabrication industrielle de pain et de pâtisserie fraîche" },
  { code: "10.71B", libelle: "Cuisson de produits de boulangerie" },
  { code: "10.71C", libelle: "Boulangerie et boulangerie-pâtisserie" },
  { code: "10.71D", libelle: "Pâtisserie" },
  { code: "10.72Z", libelle: "Fabrication de biscuits, biscottes et pâtisseries de conservation" },
  { code: "10.73Z", libelle: "Fabrication de pâtes alimentaires" },
  { code: "10.81Z", libelle: "Fabrication de sucre" },
  { code: "10.82Z", libelle: "Fabrication de cacao, chocolat et de produits de confiserie" },
  { code: "10.83Z", libelle: "Transformation du thé et du café" },
  { code: "10.84Z", libelle: "Fabrication de condiments et assaisonnements" },
  { code: "10.85Z", libelle: "Fabrication de plats préparés" },
  { code: "10.86Z", libelle: "Fabrication d'aliments homogénéisés et diététiques" },
  { code: "10.89Z", libelle: "Fabrication d'autres produits alimentaires n.c.a." },
  { code: "10.91Z", libelle: "Fabrication d'aliments pour animaux de ferme" },
  { code: "10.92Z", libelle: "Fabrication d'aliments pour animaux de compagnie" },
  { code: "11.01Z", libelle: "Production de boissons alcooliques distillées" },
  { code: "11.02A", libelle: "Fabrication de vins effervescents" },
  { code: "11.02B", libelle: "Vinification" },
  { code: "11.03Z", libelle: "Fabrication de cidre et de vins de fruits" },
  { code: "11.04Z", libelle: "Production d'autres boissons fermentées non distillées" },
  { code: "11.05Z", libelle: "Fabrication de bière" },
  { code: "11.06Z", libelle: "Fabrication de malt" },
  { code: "11.07A", libelle: "Industrie des eaux de table" },
  { code: "11.07B", libelle: "Production de boissons rafraîchissantes" },
  { code: "12.00Z", libelle: "Fabrication de produits à base de tabac" },
  { code: "13.10Z", libelle: "Préparation de fibres textiles et filature" },
  { code: "13.20Z", libelle: "Tissage" },
  { code: "13.30Z", libelle: "Ennoblissement textile" },
  { code: "13.91Z", libelle: "Fabrication d'étoffes à mailles" },
  { code: "13.92Z", libelle: "Fabrication d'articles textiles, sauf habillement" },
  { code: "13.93Z", libelle: "Fabrication de tapis et moquettes" },
  { code: "13.94Z", libelle: "Fabrication de ficelles, cordes et filets" },
  { code: "13.95Z", libelle: "Fabrication de non-tissés, sauf habillement" },
  { code: "13.96Z", libelle: "Fabrication d'autres textiles techniques et industriels" },
  { code: "13.99Z", libelle: "Fabrication d'autres textiles n.c.a." },
  { code: "14.11Z", libelle: "Fabrication de vêtements en cuir" },
  { code: "14.12Z", libelle: "Fabrication de vêtements de travail" },
  { code: "14.13Z", libelle: "Fabrication de vêtements de dessus" },
  { code: "14.14Z", libelle: "Fabrication de vêtements de dessous" },
  { code: "14.19Z", libelle: "Fabrication d'autres vêtements et accessoires" },
  { code: "14.20Z", libelle: "Fabrication d'articles en fourrure" },
  { code: "14.31Z", libelle: "Fabrication d'articles chaussants à mailles" },
  { code: "14.39Z", libelle: "Fabrication d'autres articles à mailles" },
  { code: "15.11Z", libelle: "Apprêt et tannage des cuirs ; préparation et teinture des fourrures" },
  { code: "15.12Z", libelle: "Fabrication d'articles de voyage, de maroquinerie et de sellerie" },
  { code: "15.20Z", libelle: "Fabrication de chaussures" },
  { code: "16.10A", libelle: "Sciage et rabotage du bois, hors imprégnation" },
  { code: "16.10B", libelle: "Imprégnation du bois" },
  { code: "16.21Z", libelle: "Fabrication de placage et de panneaux de bois" },
  { code: "16.22Z", libelle: "Fabrication de parquets assemblés" },
  { code: "16.23Z", libelle: "Fabrication de charpentes et d'autres menuiseries" },
  { code: "16.24Z", libelle: "Fabrication d'emballages en bois" },
  { code: "16.29Z", libelle: "Fabrication d'objets divers en bois ; fabrication d'objets en liège, vannerie et sparterie" },
  { code: "17.11Z", libelle: "Fabrication de pâte à papier" },
  { code: "17.12Z", libelle: "Fabrication de papier et de carton" },
  { code: "17.21A", libelle: "Fabrication de carton ondulé" },
  { code: "17.21B", libelle: "Fabrication de cartonnages" },
  { code: "17.21C", libelle: "Fabrication d'emballages en papier" },
  { code: "17.22Z", libelle: "Fabrication d'articles en papier à usage sanitaire ou domestique" },
  { code: "17.23Z", libelle: "Fabrication d'articles de papeterie" },
  { code: "17.24Z", libelle: "Fabrication de papiers peints" },
  { code: "17.29Z", libelle: "Fabrication d'autres articles en papier ou en carton" },
  { code: "18.11Z", libelle: "Imprimerie de journaux" },
  { code: "18.12Z", libelle: "Autre imprimerie (labeur)" },
  { code: "18.13Z", libelle: "Activités de pré-presse" },
  { code: "18.14Z", libelle: "Reliure et activités connexes" },
  { code: "18.20Z", libelle: "Reproduction d'enregistrements" },
  { code: "19.10Z", libelle: "Cokéfaction" },
  { code: "19.20Z", libelle: "Raffinage du pétrole" },
  { code: "20.11Z", libelle: "Fabrication de gaz industriels" },
  { code: "20.12Z", libelle: "Fabrication de colorants et de pigments" },
  { code: "20.13A", libelle: "Enrichissement et  retraitement de matières nucléaires" },
  { code: "20.13B", libelle: "Fabrication d'autres produits chimiques inorganiques de base n.c.a." },
  { code: "20.14Z", libelle: "Fabrication d'autres produits chimiques organiques de base" },
  { code: "20.15Z", libelle: "Fabrication de produits azotés et d'engrais" },
  { code: "20.16Z", libelle: "Fabrication de matières plastiques de base" },
  { code: "20.17Z", libelle: "Fabrication de caoutchouc synthétique" },
  { code: "20.20Z", libelle: "Fabrication de pesticides et d’autres produits agrochimiques" },
  { code: "20.30Z", libelle: "Fabrication de peintures, vernis, encres et mastics" },
  { code: "20.41Z", libelle: "Fabrication de savons, détergents et produits d'entretien" },
  { code: "20.42Z", libelle: "Fabrication de parfums et de produits pour la toilette" },
  { code: "20.51Z", libelle: "Fabrication de produits explosifs" },
  { code: "20.52Z", libelle: "Fabrication de colles" },
  { code: "20.53Z", libelle: "Fabrication d'huiles essentielles" },
  { code: "20.59Z", libelle: "Fabrication d'autres produits chimiques n.c.a." },
  { code: "20.60Z", libelle: "Fabrication de fibres artificielles ou synthétiques" },
  { code: "21.10Z", libelle: "Fabrication de produits pharmaceutiques de base" },
  { code: "21.20Z", libelle: "Fabrication de préparations pharmaceutiques" },
  { code: "22.11Z", libelle: "Fabrication et rechapage de pneumatiques" },
  { code: "22.19Z", libelle: "Fabrication d'autres articles en caoutchouc" },
  { code: "22.21Z", libelle: "Fabrication de plaques, feuilles, tubes et profilés en matières plastiques" },
  { code: "22.22Z", libelle: "Fabrication d'emballages en matières plastiques" },
  { code: "22.23Z", libelle: "Fabrication d'éléments en matières plastiques pour la construction" },
  { code: "22.29A", libelle: "Fabrication de pièces techniques à base de matières plastiques" },
  { code: "22.29B", libelle: "Fabrication de produits de consommation courante en matières plastiques" },
  { code: "23.11Z", libelle: "Fabrication de verre plat" },
  { code: "23.12Z", libelle: "Façonnage et transformation du verre plat" },
  { code: "23.13Z", libelle: "Fabrication de verre creux" },
  { code: "23.14Z", libelle: "Fabrication de fibres de verre" },
  { code: "23.19Z", libelle: "Fabrication et façonnage d'autres articles en verre, y compris verre technique" },
  { code: "23.20Z", libelle: "Fabrication de produits réfractaires" },
  { code: "23.31Z", libelle: "Fabrication de carreaux en céramique" },
  { code: "23.32Z", libelle: "Fabrication de briques, tuiles et produits de construction, en terre cuite" },
  { code: "23.41Z", libelle: "Fabrication d'articles céramiques à usage domestique ou ornemental" },
  { code: "23.42Z", libelle: "Fabrication d'appareils sanitaires en céramique" },
  { code: "23.43Z", libelle: "Fabrication d'isolateurs et pièces isolantes en céramique" },
  { code: "23.44Z", libelle: "Fabrication d'autres produits céramiques à usage technique" },
  { code: "23.49Z", libelle: "Fabrication d'autres produits céramiques" },
  { code: "23.51Z", libelle: "Fabrication de ciment" },
  { code: "23.52Z", libelle: "Fabrication de chaux et plâtre" },
  { code: "23.61Z", libelle: "Fabrication d'éléments en béton pour la construction" },
  { code: "23.62Z", libelle: "Fabrication d'éléments en plâtre pour la construction" },
  { code: "23.63Z", libelle: "Fabrication de béton prêt à l'emploi" },
  { code: "23.64Z", libelle: "Fabrication de mortiers et bétons secs" },
  { code: "23.65Z", libelle: "Fabrication d'ouvrages en fibre-ciment" },
  { code: "23.69Z", libelle: "Fabrication d'autres ouvrages en béton, en ciment ou en plâtre" },
  { code: "23.70Z", libelle: "Taille, façonnage et finissage de pierres" },
  { code: "23.91Z", libelle: "Fabrication de produits abrasifs" },
  { code: "23.99Z", libelle: "Fabrication d'autres produits minéraux non métalliques n.c.a." },
  { code: "24.10Z", libelle: "Sidérurgie" },
  { code: "24.20Z", libelle: "Fabrication de tubes, tuyaux, profilés creux et accessoires correspondants en acier" },
  { code: "24.31Z", libelle: "Étirage à froid de barres" },
  { code: "24.32Z", libelle: "Laminage à froid de feuillards" },
  { code: "24.33Z", libelle: "Profilage à froid par formage ou pliage" },
  { code: "24.34Z", libelle: "Tréfilage à froid" },
  { code: "24.41Z", libelle: "Production de métaux précieux" },
  { code: "24.42Z", libelle: "Métallurgie de l'aluminium" },
  { code: "24.43Z", libelle: "Métallurgie du plomb, du zinc ou de l'étain" },
  { code: "24.44Z", libelle: "Métallurgie du cuivre" },
  { code: "24.45Z", libelle: "Métallurgie des autres métaux non ferreux" },
  { code: "24.46Z", libelle: "Élaboration et transformation de matières nucléaires" },
  { code: "24.51Z", libelle: "Fonderie de fonte" },
  { code: "24.52Z", libelle: "Fonderie d'acier" },
  { code: "24.53Z", libelle: "Fonderie de métaux légers" },
  { code: "24.54Z", libelle: "Fonderie d'autres métaux non ferreux" },
  { code: "25.11Z", libelle: "Fabrication de structures métalliques et de parties de structures" },
  { code: "25.12Z", libelle: "Fabrication de portes et fenêtres en métal" },
  { code: "25.21Z", libelle: "Fabrication de radiateurs et de chaudières pour le chauffage central" },
  { code: "25.29Z", libelle: "Fabrication d'autres réservoirs, citernes et conteneurs métalliques" },
  { code: "25.30Z", libelle: "Fabrication de générateurs de vapeur, à l'exception des chaudières pour le chauffage central" },
  { code: "25.40Z", libelle: "Fabrication d'armes et de munitions" },
  { code: "25.50A", libelle: "Forge, estampage, matriçage ; métallurgie des poudres" },
  { code: "25.50B", libelle: "Découpage, emboutissage" },
  { code: "25.61Z", libelle: "Traitement et revêtement des métaux" },
  { code: "25.62A", libelle: "Décolletage" },
  { code: "25.62B", libelle: "Mécanique industrielle" },
  { code: "25.71Z", libelle: "Fabrication de coutellerie" },
  { code: "25.72Z", libelle: "Fabrication de serrures et de ferrures" },
  { code: "25.73A", libelle: "Fabrication de moules et modèles" },
  { code: "25.73B", libelle: "Fabrication d'autres outillages" },
  { code: "25.91Z", libelle: "Fabrication de fûts et emballages métalliques similaires" },
  { code: "25.92Z", libelle: "Fabrication d'emballages métalliques légers" },
  { code: "25.93Z", libelle: "Fabrication d'articles en fils métalliques, de chaînes et de ressorts" },
  { code: "25.94Z", libelle: "Fabrication de vis et de boulons" },
  { code: "25.99A", libelle: "Fabrication d'articles métalliques ménagers" },
  { code: "25.99B", libelle: "Fabrication d'autres articles métalliques" },
  { code: "26.11Z", libelle: "Fabrication de composants électroniques" },
  { code: "26.12Z", libelle: "Fabrication de cartes électroniques assemblées" },
  { code: "26.20Z", libelle: "Fabrication d'ordinateurs et d'équipements périphériques" },
  { code: "26.30Z", libelle: "Fabrication d'équipements de communication" },
  { code: "26.40Z", libelle: "Fabrication de produits électroniques grand public" },
  { code: "26.51A", libelle: "Fabrication d'équipements d'aide à la navigation" },
  { code: "26.51B", libelle: "Fabrication d'instrumentation scientifique et technique" },
  { code: "26.52Z", libelle: "Horlogerie" },
  { code: "26.60Z", libelle: "Fabrication d'équipements d'irradiation médicale, d'équipements électromédicaux et électrothérapeutiques" },
  { code: "26.70Z", libelle: "Fabrication de matériels optique et photographique" },
  { code: "26.80Z", libelle: "Fabrication de supports magnétiques et optiques" },
  { code: "27.11Z", libelle: "Fabrication de moteurs, génératrices et transformateurs électriques" },
  { code: "27.12Z", libelle: "Fabrication de matériel de distribution et de commande électrique" },
  { code: "27.20Z", libelle: "Fabrication de piles et d'accumulateurs électriques" },
  { code: "27.31Z", libelle: "Fabrication de câbles de fibres optiques" },
  { code: "27.32Z", libelle: "Fabrication d'autres fils et câbles électroniques ou électriques" },
  { code: "27.33Z", libelle: "Fabrication de matériel d'installation électrique" },
  { code: "27.40Z", libelle: "Fabrication d'appareils d'éclairage électrique" },
  { code: "27.51Z", libelle: "Fabrication d'appareils électroménagers" },
  { code: "27.52Z", libelle: "Fabrication d'appareils ménagers non électriques" },
  { code: "27.90Z", libelle: "Fabrication d'autres matériels électriques" },
  { code: "28.11Z", libelle: "Fabrication de moteurs et turbines, à l'exception des moteurs d’avions et de véhicules" },
  { code: "28.12Z", libelle: "Fabrication d'équipements hydrauliques et pneumatiques" },
  { code: "28.13Z", libelle: "Fabrication d'autres pompes et compresseurs" },
  { code: "28.14Z", libelle: "Fabrication d'autres articles de robinetterie" },
  { code: "28.15Z", libelle: "Fabrication d'engrenages et d'organes mécaniques de transmission" },
  { code: "28.21Z", libelle: "Fabrication de fours et brûleurs" },
  { code: "28.22Z", libelle: "Fabrication de matériel de levage et de manutention" },
  { code: "28.23Z", libelle: "Fabrication de machines et d'équipements de bureau (à l'exception des ordinateurs et équipements périphériques)" },
  { code: "28.24Z", libelle: "Fabrication d'outillage portatif à moteur incorporé" },
  { code: "28.25Z", libelle: "Fabrication d'équipements aérauliques et frigorifiques industriels" },
  { code: "28.29A", libelle: "Fabrication d'équipements d'emballage, de conditionnement et de pesage" },
  { code: "28.29B", libelle: "Fabrication d'autres machines d'usage général" },
  { code: "28.30Z", libelle: "Fabrication de machines agricoles et forestières" },
  { code: "28.41Z", libelle: "Fabrication de machines-outils pour le travail des métaux" },
  { code: "28.49Z", libelle: "Fabrication d'autres machines-outils" },
  { code: "28.91Z", libelle: "Fabrication de machines pour la métallurgie" },
  { code: "28.92Z", libelle: "Fabrication de machines pour l'extraction ou la construction" },
  { code: "28.93Z", libelle: "Fabrication de machines pour l'industrie agro-alimentaire" },
  { code: "28.94Z", libelle: "Fabrication de machines pour les industries textiles" },
  { code: "28.95Z", libelle: "Fabrication de machines pour les industries du papier et du carton" },
  { code: "28.96Z", libelle: "Fabrication de machines pour le travail du caoutchouc ou des plastiques" },
  { code: "28.99A", libelle: "Fabrication de machines d'imprimerie" },
  { code: "28.99B", libelle: "Fabrication d'autres machines spécialisées" },
  { code: "29.10Z", libelle: "Construction de véhicules automobiles" },
  { code: "29.20Z", libelle: "Fabrication de carrosseries et remorques" },
  { code: "29.31Z", libelle: "Fabrication d'équipements électriques et électroniques automobiles" },
  { code: "29.32Z", libelle: "Fabrication d'autres équipements automobiles" },
  { code: "30.11Z", libelle: "Construction de navires et de structures flottantes" },
  { code: "30.12Z", libelle: "Construction de bateaux de plaisance" },
  { code: "30.20Z", libelle: "Construction de locomotives et d'autre matériel ferroviaire roulant" },
  { code: "30.30Z", libelle: "Construction aéronautique et spatiale" },
  { code: "30.40Z", libelle: "Construction de véhicules militaires de combat" },
  { code: "30.91Z", libelle: "Fabrication de motocycles" },
  { code: "30.92Z", libelle: "Fabrication de bicyclettes et de véhicules pour invalides" },
  { code: "30.99Z", libelle: "Fabrication d’autres équipements de transport n.c.a." },
  { code: "31.01Z", libelle: "Fabrication de meubles de bureau et de magasin" },
  { code: "31.02Z", libelle: "Fabrication de meubles de cuisine" },
  { code: "31.03Z", libelle: "Fabrication de matelas" },
  { code: "31.09A", libelle: "Fabrication de sièges d'ameublement d'intérieur" },
  { code: "31.09B", libelle: "Fabrication d’autres meubles et industries connexes de l’ameublement" },
  { code: "32.11Z", libelle: "Frappe de monnaie" },
  { code: "32.12Z", libelle: "Fabrication d’articles de joaillerie et bijouterie" },
  { code: "32.13Z", libelle: "Fabrication d’articles de bijouterie fantaisie et articles similaires" },
  { code: "32.20Z", libelle: "Fabrication d'instruments de musique" },
  { code: "32.30Z", libelle: "Fabrication d'articles de sport" },
  { code: "32.40Z", libelle: "Fabrication de jeux et jouets" },
  { code: "32.50A", libelle: "Fabrication de matériel médico-chirurgical et dentaire" },
  { code: "32.50B", libelle: "Fabrication de lunettes" },
  { code: "32.91Z", libelle: "Fabrication d’articles de brosserie" },
  { code: "32.99Z", libelle: "Autres activités manufacturières n.c.a." },
  { code: "33.11Z", libelle: "Réparation d'ouvrages en métaux" },
  { code: "33.12Z", libelle: "Réparation de machines et équipements mécaniques" },
  { code: "33.13Z", libelle: "Réparation de matériels électroniques et optiques" },
  { code: "33.14Z", libelle: "Réparation d'équipements électriques" },
  { code: "33.15Z", libelle: "Réparation et maintenance navale" },
  { code: "33.16Z", libelle: "Réparation et maintenance d'aéronefs et d'engins spatiaux" },
  { code: "33.17Z", libelle: "Réparation et maintenance d'autres équipements de transport" },
  { code: "33.19Z", libelle: "Réparation d'autres équipements" },
  { code: "33.20A", libelle: "Installation de structures métalliques, chaudronnées et de tuyauterie" },
  { code: "33.20B", libelle: "Installation de machines et équipements mécaniques" },
  { code: "33.20C", libelle: "Conception d'ensemble et assemblage sur site industriel d'équipements de contrôle des processus industriels" },
  { code: "33.20D", libelle: "Installation d'équipements électriques, de matériels électroniques et optiques ou d'autres matériels" },
  { code: "35.11Z", libelle: "Production d'électricité" },
  { code: "35.12Z", libelle: "Transport d'électricité" },
  { code: "35.13Z", libelle: "Distribution d'électricité" },
  { code: "35.14Z", libelle: "Commerce d'électricité" },
  { code: "35.21Z", libelle: "Production de combustibles gazeux" },
  { code: "35.22Z", libelle: "Distribution de combustibles gazeux par conduites" },
  { code: "35.23Z", libelle: "Commerce de combustibles gazeux par conduites" },
  { code: "35.30Z", libelle: "Production et distribution de vapeur et d'air conditionné" },
  { code: "36.00Z", libelle: "Captage, traitement et distribution d'eau" },
  { code: "37.00Z", libelle: "Collecte et traitement des eaux usées" },
  { code: "38.11Z", libelle: "Collecte des déchets non dangereux" },
  { code: "38.12Z", libelle: "Collecte des déchets dangereux" },
  { code: "38.21Z", libelle: "Traitement et élimination des déchets non dangereux" },
  { code: "38.22Z", libelle: "Traitement et élimination des déchets dangereux" },
  { code: "38.31Z", libelle: "Démantèlement d'épaves" },
  { code: "38.32Z", libelle: "Récupération de déchets triés" },
  { code: "39.00Z", libelle: "Dépollution et autres services de gestion des déchets" },
  { code: "41.10A", libelle: "Promotion immobilière de logements" },
  { code: "41.10B", libelle: "Promotion immobilière de bureaux" },
  { code: "41.10C", libelle: "Promotion immobilière d'autres bâtiments" },
  { code: "41.10D", libelle: "Supports juridiques de programmes" },
  { code: "41.20A", libelle: "Construction de maisons individuelles" },
  { code: "41.20B", libelle: "Construction d'autres bâtiments" },
  { code: "42.11Z", libelle: "Construction de routes et autoroutes" },
  { code: "42.12Z", libelle: "Construction de voies ferrées de surface et souterraines" },
  { code: "42.13A", libelle: "Construction d'ouvrages d'art" },
  { code: "42.13B", libelle: "Construction et entretien de tunnels" },
  { code: "42.21Z", libelle: "Construction de réseaux pour fluides" },
  { code: "42.22Z", libelle: "Construction de réseaux électriques et de télécommunications" },
  { code: "42.91Z", libelle: "Construction d'ouvrages maritimes et fluviaux" },
  { code: "42.99Z", libelle: "Construction d'autres ouvrages de génie civil n.c.a." },
  { code: "43.11Z", libelle: "Travaux de démolition" },
  { code: "43.12A", libelle: "Travaux de terrassement courants et travaux préparatoires" },
  { code: "43.12B", libelle: "Travaux de terrassement spécialisés ou de grande masse" },
  { code: "43.13Z", libelle: "Forages et sondages" },
  { code: "43.21A", libelle: "Travaux d'installation électrique dans tous locaux" },
  { code: "43.21B", libelle: "Travaux d'installation électrique sur la voie publique" },
  { code: "43.22A", libelle: "Travaux d'installation d'eau et de gaz en tous locaux" },
  { code: "43.22B", libelle: "Travaux d'installation d'équipements thermiques et de climatisation" },
  { code: "43.29A", libelle: "Travaux d'isolation" },
  { code: "43.29B", libelle: "Autres travaux d'installation n.c.a." },
  { code: "43.31Z", libelle: "Travaux de plâtrerie" },
  { code: "43.32A", libelle: "Travaux de menuiserie bois et PVC" },
  { code: "43.32B", libelle: "Travaux de menuiserie métallique et serrurerie" },
  { code: "43.32C", libelle: "Agencement de lieux de vente" },
  { code: "43.33Z", libelle: "Travaux de revêtement des sols et des murs" },
  { code: "43.34Z", libelle: "Travaux de peinture et vitrerie" },
  { code: "43.39Z", libelle: "Autres travaux de finition" },
  { code: "43.91A", libelle: "Travaux de charpente" },
  { code: "43.91B", libelle: "Travaux de couverture par éléments" },
  { code: "43.99A", libelle: "Travaux d'étanchéification" },
  { code: "43.99B", libelle: "Travaux de montage de structures métalliques" },
  { code: "43.99C", libelle: "Travaux de maçonnerie générale et gros œuvre de bâtiment" },
  { code: "43.99D", libelle: "Autres travaux spécialisés de construction" },
  { code: "43.99E", libelle: "Location avec opérateur de matériel de construction" },
  { code: "45.11Z", libelle: "Commerce de voitures et de véhicules automobiles légers" },
  { code: "45.19Z", libelle: "Commerce d'autres véhicules automobiles" },
  { code: "45.20A", libelle: "Entretien et réparation de véhicules automobiles légers" },
  { code: "45.20B", libelle: "Entretien et réparation d'autres véhicules automobiles" },
  { code: "45.31Z", libelle: "Commerce de gros d'équipements automobiles" },
  { code: "45.32Z", libelle: "Commerce de détail d'équipements automobiles" },
  { code: "45.40Z", libelle: "Commerce et réparation de motocycles" },
  { code: "46.11Z", libelle: "Intermédiaires du commerce en matières premières agricoles, animaux vivants, matières premières textiles et produits semi-finis" },
  { code: "46.12A", libelle: "Centrales d'achat de carburant" },
  { code: "46.12B", libelle: "Autres intermédiaires du commerce en combustibles, métaux, minéraux et produits chimiques" },
  { code: "46.13Z", libelle: "Intermédiaires du commerce en bois et matériaux de construction" },
  { code: "46.14Z", libelle: "Intermédiaires du commerce en machines, équipements industriels, navires et avions" },
  { code: "46.15Z", libelle: "Intermédiaires du commerce en meubles, articles de ménage et quincaillerie" },
  { code: "46.16Z", libelle: "Intermédiaires du commerce en textiles, habillement, fourrures, chaussures et articles en cuir" },
  { code: "46.17A", libelle: "Centrales d'achat alimentaires" },
  { code: "46.17B", libelle: "Autres intermédiaires du commerce en denrées, boissons et tabac" },
  { code: "46.18Z", libelle: "Intermédiaires spécialisés dans le commerce d'autres produits spécifiques" },
  { code: "46.19A", libelle: "Centrales d'achat non alimentaires" },
  { code: "46.19B", libelle: "Autres intermédiaires du commerce en produits divers" },
  { code: "46.21Z", libelle: "Commerce de gros (commerce interentreprises) de céréales, de tabac non manufacturé, de semences et d'aliments pour le bétail" },
  { code: "46.22Z", libelle: "Commerce de gros (commerce interentreprises) de fleurs et plantes" },
  { code: "46.23Z", libelle: "Commerce de gros (commerce interentreprises) d'animaux vivants" },
  { code: "46.24Z", libelle: "Commerce de gros (commerce interentreprises) de cuirs et peaux" },
  { code: "46.31Z", libelle: "Commerce de gros (commerce interentreprises) de fruits et légumes" },
  { code: "46.32A", libelle: "Commerce de gros (commerce interentreprises) de viandes de boucherie" },
  { code: "46.32B", libelle: "Commerce de gros (commerce interentreprises) de produits à base de viande" },
  { code: "46.32C", libelle: "Commerce de gros (commerce interentreprises) de volailles et gibier" },
  { code: "46.33Z", libelle: "Commerce de gros (commerce interentreprises) de produits laitiers, œufs, huiles et matières grasses comestibles" },
  { code: "46.34Z", libelle: "Commerce de gros (commerce interentreprises) de boissons" },
  { code: "46.35Z", libelle: "Commerce de gros (commerce interentreprises) de produits à base de tabac" },
  { code: "46.36Z", libelle: "Commerce de gros (commerce interentreprises) de sucre, chocolat et confiserie" },
  { code: "46.37Z", libelle: "Commerce de gros (commerce interentreprises) de café, thé, cacao et épices" },
  { code: "46.38A", libelle: "Commerce de gros (commerce interentreprises) de poissons, crustacés et mollusques" },
  { code: "46.38B", libelle: "Commerce de gros (commerce interentreprises) alimentaire spécialisé divers" },
  { code: "46.39A", libelle: "Commerce de gros (commerce interentreprises) de produits surgelés" },
  { code: "46.39B", libelle: "Commerce de gros (commerce interentreprises) alimentaire non spécialisé" },
  { code: "46.41Z", libelle: "Commerce de gros (commerce interentreprises) de textiles" },
  { code: "46.42Z", libelle: "Commerce de gros (commerce interentreprises) d'habillement et de chaussures" },
  { code: "46.43Z", libelle: "Commerce de gros (commerce interentreprises) d'appareils électroménagers" },
  { code: "46.44Z", libelle: "Commerce de gros (commerce interentreprises) de vaisselle, verrerie et produits d'entretien" },
  { code: "46.45Z", libelle: "Commerce de gros (commerce interentreprises) de parfumerie et de produits de beauté" },
  { code: "46.46Z", libelle: "Commerce de gros (commerce interentreprises) de produits pharmaceutiques" },
  { code: "46.47Z", libelle: "Commerce de gros (commerce interentreprises) de meubles, de tapis et d'appareils d'éclairage" },
  { code: "46.48Z", libelle: "Commerce de gros (commerce interentreprises) d'articles d'horlogerie et de bijouterie" },
  { code: "46.49Z", libelle: "Commerce de gros (commerce interentreprises) d'autres biens domestiques" },
  { code: "46.51Z", libelle: "Commerce de gros (commerce interentreprises) d'ordinateurs, d'équipements informatiques périphériques et de logiciels" },
  { code: "46.52Z", libelle: "Commerce de gros (commerce interentreprises) de composants et d'équipements électroniques et de télécommunication" },
  { code: "46.61Z", libelle: "Commerce de gros (commerce interentreprises) de matériel agricole" },
  { code: "46.62Z", libelle: "Commerce de gros (commerce interentreprises) de machines-outils" },
  { code: "46.63Z", libelle: "Commerce de gros (commerce interentreprises) de machines pour l'extraction, la construction et le génie civil" },
  { code: "46.64Z", libelle: "Commerce de gros (commerce interentreprises) de machines pour l'industrie textile et l'habillement" },
  { code: "46.65Z", libelle: "Commerce de gros (commerce interentreprises) de mobilier de bureau" },
  { code: "46.66Z", libelle: "Commerce de gros (commerce interentreprises) d'autres machines et équipements de bureau" },
  { code: "46.69A", libelle: "Commerce de gros (commerce interentreprises) de matériel électrique" },
  { code: "46.69B", libelle: "Commerce de gros (commerce interentreprises) de fournitures et équipements industriels divers" },
  { code: "46.69C", libelle: "Commerce de gros (commerce interentreprises) de fournitures et équipements divers pour le commerce et les services" },
  { code: "46.71Z", libelle: "Commerce de gros (commerce interentreprises) de combustibles et de produits annexes" },
  { code: "46.72Z", libelle: "Commerce de gros (commerce interentreprises) de minerais et métaux" },
  { code: "46.73A", libelle: "Commerce de gros (commerce interentreprises) de bois et de matériaux de construction" },
  { code: "46.73B", libelle: "Commerce de gros (commerce interentreprises) d'appareils sanitaires et de produits de décoration" },
  { code: "46.74A", libelle: "Commerce de gros (commerce interentreprises) de quincaillerie" },
  { code: "46.74B", libelle: "Commerce de gros (commerce interentreprises) de fournitures pour la plomberie et le chauffage" },
  { code: "46.75Z", libelle: "Commerce de gros (commerce interentreprises) de produits chimiques" },
  { code: "46.76Z", libelle: "Commerce de gros (commerce interentreprises) d'autres produits intermédiaires" },
  { code: "46.77Z", libelle: "Commerce de gros (commerce interentreprises) de déchets et débris" },
  { code: "46.90Z", libelle: "Commerce de gros (commerce interentreprises) non spécialisé" },
  { code: "47.11A", libelle: "Commerce de détail de produits surgelés" },
  { code: "47.11B", libelle: "Commerce d'alimentation générale" },
  { code: "47.11C", libelle: "Supérettes" },
  { code: "47.11D", libelle: "Supermarchés" },
  { code: "47.11E", libelle: "Magasins multi-commerces" },
  { code: "47.11F", libelle: "Hypermarchés" },
  { code: "47.19A", libelle: "Grands magasins" },
  { code: "47.19B", libelle: "Autres commerces de détail en magasin non spécialisé" },
  { code: "47.21Z", libelle: "Commerce de détail de fruits et légumes en magasin spécialisé" },
  { code: "47.22Z", libelle: "Commerce de détail de viandes et de produits à base de viande en magasin spécialisé" },
  { code: "47.23Z", libelle: "Commerce de détail de poissons, crustacés et mollusques en magasin spécialisé" },
  { code: "47.24Z", libelle: "Commerce de détail de pain, pâtisserie et confiserie en magasin spécialisé" },
  { code: "47.25Z", libelle: "Commerce de détail de boissons en magasin spécialisé" },
  { code: "47.26Z", libelle: "Commerce de détail de produits à base de tabac en magasin spécialisé" },
  { code: "47.29Z", libelle: "Autres commerces de détail alimentaires en magasin spécialisé" },
  { code: "47.29Z", libelle: "Autres commerces de détail alimentaires en magasin spécialisé" },
  { code: "47.30Z", libelle: "Commerce de détail de carburants en magasin spécialisé" },
  { code: "47.41Z", libelle: "Commerce de détail d'ordinateurs, d'unités périphériques et de logiciels en magasin spécialisé" },
  { code: "47.42Z", libelle: "Commerce de détail de matériels de télécommunication en magasin spécialisé" },
  { code: "47.43Z", libelle: "Commerce de détail de matériels audio et vidéo en magasin spécialisé" },
  { code: "47.51Z", libelle: "Commerce de détail de textiles en magasin spécialisé" },
  { code: "47.52A", libelle: "Commerce de détail de quincaillerie, peintures et verres en petites surfaces (moins de 400 m2)" },
  { code: "47.52B", libelle: "Commerce de détail de quincaillerie, peintures et verres en grandes surfaces (400 m2et plus)" },
  { code: "47.53Z", libelle: "Commerce de détail de tapis, moquettes et revêtements de murs et de sols en magasin spécialisé" },
  { code: "47.54Z", libelle: "Commerce de détail d'appareils électroménagers en magasin spécialisé" },
  { code: "47.59A", libelle: "Commerce de détail de meubles" },
  { code: "47.59B", libelle: "Commerce de détail d'autres équipements du foyer" },
  { code: "47.61Z", libelle: "Commerce de détail de livres en magasin spécialisé" },
  { code: "47.62Z", libelle: "Commerce de détail de journaux et papeterie en magasin spécialisé" },
  { code: "47.63Z", libelle: "Commerce de détail d'enregistrements musicaux et vidéo en magasin spécialisé" },
  { code: "47.64Z", libelle: "Commerce de détail d'articles de sport en magasin spécialisé" },
  { code: "47.65Z", libelle: "Commerce de détail de jeux et jouets en magasin spécialisé" },
  { code: "47.71Z", libelle: "Commerce de détail d'habillement en magasin spécialisé" },
  { code: "47.72A", libelle: "Commerce de détail de la chaussure" },
  { code: "47.72B", libelle: "Commerce de détail de maroquinerie et d'articles de voyage" },
  { code: "47.73Z", libelle: "Commerce de détail de produits pharmaceutiques en magasin spécialisé" },
  { code: "47.74Z", libelle: "Commerce de détail d'articles médicaux et orthopédiques en magasin spécialisé" },
  { code: "47.75Z", libelle: "Commerce de détail de parfumerie et de produits de beauté en magasin spécialisé" },
  { code: "47.76Z", libelle: "Commerce de détail de fleurs, plantes, graines, engrais, animaux de compagnie et aliments pour ces animaux en magasin spécialisé" },
  { code: "47.77Z", libelle: "Commerce de détail d'articles d'horlogerie et de bijouterie en magasin spécialisé" },
  { code: "47.78A", libelle: "Commerces de détail d'optique" },
  { code: "47.78B", libelle: "Commerces de détail de charbons et combustibles" },
  { code: "47.78C", libelle: "Autres commerces de détail spécialisés divers" },
  { code: "47.79Z", libelle: "Commerce de détail de biens d'occasion en magasin" },
  { code: "47.81Z", libelle: "Commerce de détail alimentaire sur éventaires et marchés" },
  { code: "47.82Z", libelle: "Commerce de détail de textiles, d'habillement et de chaussures sur éventaires et marchés" },
  { code: "47.89Z", libelle: "Autres commerces de détail sur éventaires et marchés" },
  { code: "47.91A", libelle: "Vente à distance sur catalogue général" },
  { code: "47.91B", libelle: "Vente à distance sur catalogue spécialisé" },
  { code: "47.99A", libelle: "Vente à domicile" },
  { code: "47.99B", libelle: "Vente par automates et autres commerces de détail hors magasin, éventaires ou marchés n.c.a." },
  { code: "49.10Z", libelle: "Transport ferroviaire interurbain de voyageurs" },
  { code: "49.20Z", libelle: "Transports ferroviaires de fret" },
  { code: "49.31Z", libelle: "Transports urbains et suburbains de voyageurs" },
  { code: "49.32Z", libelle: "Transports de voyageurs par taxis" },
  { code: "49.39A", libelle: "Transports routiers réguliers de voyageurs" },
  { code: "49.39B", libelle: "Autres transports routiers de voyageurs" },
  { code: "49.39C", libelle: "Téléphériques et remontées mécaniques" },
  { code: "49.41A", libelle: "Transports routiers de fret interurbains" },
  { code: "49.41B", libelle: "Transports routiers de fret de proximité" },
  { code: "49.41C", libelle: "Location de camions avec chauffeur" },
  { code: "49.42Z", libelle: "Services de déménagement" },
  { code: "49.50Z", libelle: "Transports par conduites" },
  { code: "50.10Z", libelle: "Transports maritimes et côtiers de passagers" },
  { code: "50.20Z", libelle: "Transports maritimes et côtiers de fret" },
  { code: "50.30Z", libelle: "Transports fluviaux de passagers" },
  { code: "50.40Z", libelle: "Transports fluviaux de fret" },
  { code: "51.10Z", libelle: "Transports aériens de passagers" },
  { code: "51.21Z", libelle: "Transports aériens de fret" },
  { code: "51.22Z", libelle: "Transports spatiaux" },
  { code: "52.10A", libelle: "Entreposage et stockage frigorifique" },
  { code: "52.10B", libelle: "Entreposage et stockage non frigorifique" },
  { code: "52.21Z", libelle: "Services auxiliaires des transports terrestres" },
  { code: "52.22Z", libelle: "Services auxiliaires des transports par eau" },
  { code: "52.23Z", libelle: "Services auxiliaires des transports aériens" },
  { code: "52.24A", libelle: "Manutention portuaire" },
  { code: "52.24B", libelle: "Manutention non portuaire" },
  { code: "52.29A", libelle: "Messagerie, fret express" },
  { code: "52.29B", libelle: "Affrètement et organisation des transports" },
  { code: "53.10Z", libelle: "Activités de poste dans le cadre d'une obligation de service universel" },
  { code: "53.20Z", libelle: "Autres activités de poste et de courrier" },
  { code: "55.10Z", libelle: "Hôtels et hébergement similaire" },
  { code: "55.20Z", libelle: "Hébergement touristique et autre hébergement de courte durée" },
  { code: "55.30Z", libelle: "Terrains de camping et parcs pour caravanes ou véhicules de loisirs" },
  { code: "55.90Z", libelle: "Autres hébergements" },
  { code: "56.10A", libelle: "Restauration traditionnelle" },
  { code: "56.10B", libelle: "Cafétérias et autres libres-services" },
  { code: "56.10C", libelle: "Restauration de type rapide" },
  { code: "56.21Z", libelle: "Services des traiteurs" },
  { code: "56.29A", libelle: "Restauration collective sous contrat" },
  { code: "56.29B", libelle: "Autres services de restauration n.c.a." },
  { code: "56.30Z", libelle: "Débits de boissons" },
  { code: "58.11Z", libelle: "Édition de livres" },
  { code: "58.12Z", libelle: "Édition de répertoires et de fichiers d'adresses" },
  { code: "58.13Z", libelle: "Édition de journaux" },
  { code: "58.14Z", libelle: "Édition de revues et périodiques" },
  { code: "58.19Z", libelle: "Autres activités d'édition" },
  { code: "58.21Z", libelle: "Édition de jeux électroniques" },
  { code: "58.29A", libelle: "Édition de logiciels système et de réseau" },
  { code: "58.29B", libelle: "Edition de logiciels outils de développement et de langages" },
  { code: "58.29C", libelle: "Edition de logiciels applicatifs" },
  { code: "59.11A", libelle: "Production de films et de programmes pour la télévision" },
  { code: "59.11B", libelle: "Production de films institutionnels et publicitaires" },
  { code: "59.11C", libelle: "Production de films pour le cinéma" },
  { code: "59.12Z", libelle: "Post-production de films cinématographiques, de vidéo et de programmes de télévision" },
  { code: "59.13A", libelle: "Distribution de films cinématographiques" },
  { code: "59.13B", libelle: "Edition et distribution vidéo" },
  { code: "59.14Z", libelle: "Projection de films cinématographiques" },
  { code: "59.20Z", libelle: "Enregistrement sonore et édition musicale" },
  { code: "60.10Z", libelle: "Édition et diffusion de programmes radio" },
  { code: "60.20A", libelle: "Edition de chaînes généralistes" },
  { code: "60.20B", libelle: "Edition de chaînes thématiques" },
  { code: "61.10Z", libelle: "Télécommunications filaires" },
  { code: "61.20Z", libelle: "Télécommunications sans fil" },
  { code: "61.30Z", libelle: "Télécommunications par satellite" },
  { code: "61.90Z", libelle: "Autres activités de télécommunication" },
  { code: "62.01Z", libelle: "Programmation informatique" },
  { code: "62.02A", libelle: "Conseil en systèmes et logiciels informatiques" },
  { code: "62.02B", libelle: "Tierce maintenance de systèmes et d’applications informatiques" },
  { code: "62.03Z", libelle: "Gestion d'installations informatiques" },
  { code: "62.09Z", libelle: "Autres activités informatiques" },
  { code: "63.11Z", libelle: "Traitement de données, hébergement et activités connexes" },
  { code: "63.12Z", libelle: "Portails Internet" },
  { code: "63.91Z", libelle: "Activités des agences de presse" },
  { code: "63.99Z", libelle: "Autres services d'information n.c.a." },
  { code: "64.11Z", libelle: "Activités de banque centrale" },
  { code: "64.19Z", libelle: "Autres intermédiations monétaires" },
  { code: "64.20Z", libelle: "Activités des sociétés holding" },
  { code: "64.30Z", libelle: "Fonds de placement et entités financières similaires" },
  { code: "64.91Z", libelle: "Crédit-bail" },
  { code: "64.92Z", libelle: "Autre distribution de crédit" },
  { code: "64.99Z", libelle: "Autres activités des services financiers, hors assurance et caisses de retraite, n.c.a." },
  { code: "65.11Z", libelle: "Assurance vie" },
  { code: "65.12Z", libelle: "Autres assurances" },
  { code: "65.20Z", libelle: "Réassurance" },
  { code: "65.30Z", libelle: "Caisses de retraite" },
  { code: "66.11Z", libelle: "Administration de marchés financiers" },
  { code: "66.12Z", libelle: "Courtage de valeurs mobilières et de marchandises" },
  { code: "66.19A", libelle: "Supports juridiques de gestion de patrimoine mobilier" },
  { code: "66.19B", libelle: "Autres activités auxiliaires de services financiers, hors assurance et caisses de retraite, n.c.a." },
  { code: "66.21Z", libelle: "Évaluation des risques et dommages" },
  { code: "66.22Z", libelle: "Activités des agents et courtiers d'assurances" },
  { code: "66.29Z", libelle: "Autres activités auxiliaires d'assurance et de caisses de retraite" },
  { code: "66.30Z", libelle: "Gestion de fonds" },
  { code: "68.10Z", libelle: "Activités des marchands de biens immobiliers" },
  { code: "68.20A", libelle: "Location de logements" },
  { code: "68.20B", libelle: "Location de terrains et d'autres biens immobiliers" },
  { code: "68.31Z", libelle: "Agences immobilières" },
  { code: "68.32A", libelle: "Administration d'immeubles et autres biens immobiliers" },
  { code: "68.32B", libelle: "Supports juridiques de gestion de patrimoine immobilier" },
  { code: "69.10Z", libelle: "Activités juridiques" },
  { code: "69.20Z", libelle: "Activités comptables" },
  { code: "70.10Z", libelle: "Activités des sièges sociaux" },
  { code: "70.21Z", libelle: "Conseil en relations publiques et communication" },
  { code: "70.22Z", libelle: "Conseil pour les affaires et autres conseils de gestion" },
  { code: "71.11Z", libelle: "Activités d'architecture" },
  { code: "71.12A", libelle: "Activité des géomètres" },
  { code: "71.12B", libelle: "Ingénierie, études techniques" },
  { code: "71.20A", libelle: "Contrôle technique automobile" },
  { code: "71.20B", libelle: "Analyses, essais et inspections techniques" },
  { code: "72.11Z", libelle: "Recherche-développement en biotechnologie" },
  { code: "72.19Z", libelle: "Recherche-développement en autres sciences physiques et naturelles" },
  { code: "72.20Z", libelle: "Recherche-développement en sciences humaines et sociales" },
  { code: "73.11Z", libelle: "Activités des agences de publicité" },
  { code: "73.12Z", libelle: "Régie publicitaire de médias" },
  { code: "73.20Z", libelle: "Études de marché et sondages" },
  { code: "74.10Z", libelle: "Activités spécialisées de design" },
  { code: "74.20Z", libelle: "Activités photographiques" },
  { code: "74.30Z", libelle: "Traduction et interprétation" },
  { code: "74.90A", libelle: "Activité des économistes de la construction" },
  { code: "74.90B", libelle: "Activités spécialisées, scientifiques et techniques diverses" },
  { code: "75.00Z", libelle: "Activités vétérinaires" },
  { code: "77.11A", libelle: "Location de courte durée de voitures et de véhicules automobiles légers" },
  { code: "77.11B", libelle: "Location de longue durée de voitures et de véhicules automobiles légers" },
  { code: "77.12Z", libelle: "Location et location-bail de camions" },
  { code: "77.21Z", libelle: "Location et location-bail d'articles de loisirs et de sport" },
  { code: "77.22Z", libelle: "Location de vidéocassettes et disques vidéo" },
  { code: "77.29Z", libelle: "Location et location-bail d'autres biens personnels et domestiques" },
  { code: "77.31Z", libelle: "Location et location-bail de machines et équipements agricoles" },
  { code: "77.32Z", libelle: "Location et location-bail de machines et équipements pour la construction" },
  { code: "77.33Z", libelle: "Location et location-bail de machines de bureau et de matériel informatique" },
  { code: "77.34Z", libelle: "Location et location-bail de matériels de transport par eau" },
  { code: "77.35Z", libelle: "Location et location-bail de matériels de transport aérien" },
  { code: "77.39Z", libelle: "Location et location-bail d'autres machines, équipements et biens matériels n.c.a." },
  { code: "77.40Z", libelle: "Location-bail de propriété intellectuelle et de produits similaires, à l'exception des œuvres soumises à copyright" },
  { code: "78.10Z", libelle: "Activités des agences de placement de main-d'œuvre" },
  { code: "78.20Z", libelle: "Activités des agences de travail temporaire" },
  { code: "78.30Z", libelle: "Autre mise à disposition de ressources humaines" },
  { code: "79.11Z", libelle: "Activités des agences de voyage" },
  { code: "79.12Z", libelle: "Activités des voyagistes" },
  { code: "79.90Z", libelle: "Autres services de réservation et activités connexes" },
  { code: "80.10Z", libelle: "Activités de sécurité privée" },
  { code: "80.20Z", libelle: "Activités liées aux systèmes de sécurité" },
  { code: "80.30Z", libelle: "Activités d'enquête" },
  { code: "81.10Z", libelle: "Activités combinées de soutien lié aux bâtiments" },
  { code: "81.21Z", libelle: "Nettoyage courant des bâtiments" },
  { code: "81.22Z", libelle: "Autres activités de nettoyage des bâtiments et nettoyage industriel" },
  { code: "81.29A", libelle: "Désinfection, désinsectisation, dératisation" },
  { code: "81.29B", libelle: "Autres activités de nettoyage n.c.a." },
  { code: "81.30Z", libelle: "Services d'aménagement paysager" },
  { code: "82.11Z", libelle: "Services administratifs combinés de bureau" },
  { code: "82.19Z", libelle: "Photocopie, préparation de documents et autres activités spécialisées de soutien de bureau" },
  { code: "82.20Z", libelle: "Activités de centres d'appels" },
  { code: "82.30Z", libelle: "Organisation de foires, salons professionnels et congrès" },
  { code: "82.91Z", libelle: "Activités des agences de recouvrement de factures et des sociétés d'information financière sur la clientèle" },
  { code: "82.92Z", libelle: "Activités de conditionnement" },
  { code: "82.99Z", libelle: "Autres activités de soutien aux entreprises n.c.a." },
  { code: "84.11Z", libelle: "Administration publique générale" },
  { code: "84.12Z", libelle: "Administration publique (tutelle) de la santé, de la formation, de la culture et des services sociaux, autre que sécurité sociale" },
  { code: "84.13Z", libelle: "Administration publique (tutelle) des activités économiques" },
  { code: "84.21Z", libelle: "Affaires étrangères" },
  { code: "84.22Z", libelle: "Défense" },
  { code: "84.23Z", libelle: "Justice" },
  { code: "84.24Z", libelle: "Activités d’ordre public et de sécurité" },
  { code: "84.25Z", libelle: "Services du feu et de secours" },
  { code: "84.30A", libelle: "Activités générales de sécurité sociale" },
  { code: "84.30B", libelle: "Gestion des retraites complémentaires" },
  { code: "84.30C", libelle: "Distribution sociale de revenus" },
  { code: "85.10Z", libelle: "Enseignement pré-primaire" },
  { code: "85.20Z", libelle: "Enseignement primaire" },
  { code: "85.31Z", libelle: "Enseignement secondaire général" },
  { code: "85.32Z", libelle: "Enseignement secondaire technique ou professionnel" },
  { code: "85.41Z", libelle: "Enseignement post-secondaire non supérieur" },
  { code: "85.42Z", libelle: "Enseignement supérieur" },
  { code: "85.51Z", libelle: "Enseignement de disciplines sportives et d'activités de loisirs" },
  { code: "85.52Z", libelle: "Enseignement culturel" },
  { code: "85.53Z", libelle: "Enseignement de la conduite" },
  { code: "85.59A", libelle: "Formation continue d'adultes" },
  { code: "85.59B", libelle: "Autres enseignements" },
  { code: "85.60Z", libelle: "Activités de soutien à l'enseignement" },
  { code: "86.10Z", libelle: "Activités hospitalières" },
  { code: "86.21Z", libelle: "Activité des médecins généralistes" },
  { code: "86.22A", libelle: "Activités de radiodiagnostic et de radiothérapie" },
  { code: "86.22B", libelle: "Activités chirurgicales" },
  { code: "86.22C", libelle: "Autres activités des médecins spécialistes" },
  { code: "86.23Z", libelle: "Pratique dentaire" },
  { code: "86.90A", libelle: "Ambulances" },
  { code: "86.90B", libelle: "Laboratoires d'analyses médicales" },
  { code: "86.90C", libelle: "Centres de collecte et banques d'organes" },
  { code: "86.90D", libelle: "Activités des infirmiers et des sages-femmes" },
  { code: "86.90E", libelle: "Activités des professionnels de la rééducation, de l’appareillage et des pédicures-podologues" },
  { code: "86.90F", libelle: "Activités de santé humaine non classées ailleurs" },
  { code: "87.10A", libelle: "Hébergement médicalisé pour personnes âgées" },
  { code: "87.10B", libelle: "Hébergement médicalisé pour enfants handicapés" },
  { code: "87.10C", libelle: "Hébergement médicalisé pour adultes handicapés et autre hébergement médicalisé" },
  { code: "87.20A", libelle: "Hébergement social pour handicapés mentaux et malades mentaux" },
  { code: "87.20B", libelle: "Hébergement social pour toxicomanes" },
  { code: "87.30A", libelle: "Hébergement social pour personnes âgées" },
  { code: "87.30B", libelle: "Hébergement social pour handicapés  physiques" },
  { code: "87.90A", libelle: "Hébergement social pour enfants en difficultés" },
  { code: "87.90B", libelle: "Hébergement social pour adultes et familles en difficultés et autre hébergement social" },
  { code: "88.10A", libelle: "Aide à domicile" },
  { code: "88.10B", libelle: "Accueil ou accompagnement sans hébergement d’adultes handicapés ou de  personnes âgées" },
  { code: "88.10C", libelle: "Aide par le travail" },
  { code: "88.91A", libelle: "Accueil de jeunes enfants" },
  { code: "88.91B", libelle: "Accueil ou accompagnement sans hébergement d’enfants handicapés" },
  { code: "88.99A", libelle: "Autre accueil ou accompagnement sans hébergement d’enfants  et d’adolescents" },
  { code: "88.99B", libelle: "Action sociale sans hébergement n.c.a." },
  { code: "90.01Z", libelle: "Arts du spectacle vivant" },
  { code: "90.02Z", libelle: "Activités de soutien au spectacle vivant" },
  { code: "90.03A", libelle: "Création artistique relevant des arts plastiques" },
  { code: "90.03B", libelle: "Autre création artistique" },
  { code: "90.04Z", libelle: "Gestion de salles de spectacles" },
  { code: "91.01Z", libelle: "Gestion des bibliothèques et des archives" },
  { code: "91.02Z", libelle: "Gestion des musées" },
  { code: "91.03Z", libelle: "Gestion des sites et monuments historiques et des attractions touristiques similaires" },
  { code: "91.04Z", libelle: "Gestion des jardins botaniques et zoologiques et des réserves naturelles" },
  { code: "92.00Z", libelle: "Organisation de jeux de hasard et d'argent" },
  { code: "93.11Z", libelle: "Gestion d'installations sportives" },
  { code: "93.12Z", libelle: "Activités de clubs de sports" },
  { code: "93.13Z", libelle: "Activités des centres de culture physique" },
  { code: "93.19Z", libelle: "Autres activités liées au sport" },
  { code: "93.21Z", libelle: "Activités des parcs d'attractions et parcs à thèmes" },
  { code: "93.29Z", libelle: "Autres activités récréatives et de loisirs" },
  { code: "94.11Z", libelle: "Activités des organisations patronales et consulaires" },
  { code: "94.12Z", libelle: "Activités des organisations professionnelles" },
  { code: "94.20Z", libelle: "Activités des syndicats de salariés" },
  { code: "94.91Z", libelle: "Activités des organisations religieuses" },
  { code: "94.92Z", libelle: "Activités des organisations politiques" },
  { code: "94.99Z", libelle: "Autres organisations fonctionnant par adhésion volontaire" },
  { code: "95.11Z", libelle: "Réparation d'ordinateurs et d'équipements périphériques" },
  { code: "95.12Z", libelle: "Réparation d'équipements de communication" },
  { code: "95.21Z", libelle: "Réparation de produits électroniques grand public" },
  { code: "95.22Z", libelle: "Réparation d'appareils électroménagers et d'équipements pour la maison et le jardin" },
  { code: "95.23Z", libelle: "Réparation de chaussures et d'articles en cuir" },
  { code: "95.24Z", libelle: "Réparation de meubles et d'équipements du foyer" },
  { code: "95.25Z", libelle: "Réparation d'articles d'horlogerie et de bijouterie" },
  { code: "95.29Z", libelle: "Réparation d'autres biens personnels et domestiques" },
  { code: "96.01A", libelle: "Blanchisserie-teinturerie de gros" },
  { code: "96.01B", libelle: "Blanchisserie-teinturerie de détail" },
  { code: "96.02A", libelle: "Coiffure" },
  { code: "96.02B", libelle: "Soins de beauté" },
  { code: "96.03Z", libelle: "Services funéraires" },
  { code: "96.04Z", libelle: "Entretien corporel" },
  { code: "96.09Z", libelle: "Autres services personnels n.c.a." },
  { code: "97.00Z", libelle: "Activités des ménages en tant qu'employeurs de personnel domestique" },
  { code: "98.10Z", libelle: "Activités indifférenciées des ménages en tant que producteurs de biens pour usage propre" },
  { code: "98.20Z", libelle: "Activités indifférenciées des ménages en tant que producteurs de services pour usage propre" },
  { code: "99.00Z", libelle: "Activités des organisations et organismes extraterritoriaux" },
];

  const normalize = (str) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  const search = async (q) => {
    const text = normalize(q || query);
    if (!text.trim()) return;
    setLoading(true); setResults([]); setSelected(null); setError("");
    await new Promise(r => setTimeout(r, 300));

    const words = text.trim().split(/\s+/).filter(w => w.length >= 3);
    
    const scores = TOUS_CODES_APE.map(c => {
      const lib = normalize(c.libelle);
      let score = 0;
      words.forEach(w => {
        if (lib.includes(w)) score += 3;
        else if (lib.split(/\s+/).some(l => l.startsWith(w.slice(0, 4)))) score += 1;
      });
      return { ...c, score };
    }).filter(c => c.score > 0).sort((a, b) => b.score - a.score);

    if (scores.length > 0) {
      setResults(scores.slice(0, 3).map(c => ({
        code: c.code,
        libelle: c.libelle,
        section: "NAF Rév. 2",
        confiance: c.score >= 3 ? "haute" : "moyenne",
        explication: `Libellé officiel INSEE correspondant à votre activité.`,
      })));
    } else {
      setResults([
        { code: "82.11Z", libelle: "Services administratifs combinés de bureau", section: "NAF Rév. 2", confiance: "moyenne", explication: "Code générique pour les activités de services aux entreprises" },
        { code: "70.22Z", libelle: "Conseil pour les affaires et autres conseils de gestion", section: "NAF Rév. 2", confiance: "moyenne", explication: "Code pour les activités de conseil et consulting" },
        { code: "96.09Z", libelle: "Autres services personnels", section: "NAF Rév. 2", confiance: "moyenne", explication: "Code pour les activités de services personnels divers" },
      ]);
      setError("Aucun résultat précis — essayez des termes différents comme le nom du métier ou de l'activité.");
    }
    setLoading(false);
  };

  const copy = () => {
    if (!selected) return;
    navigator.clipboard.writeText(selected.code);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  const inputStyle = { width: "100%", border: "1.5px solid #e5d5f5", borderRadius: 10, padding: "12px 16px", fontFamily: "'DM Sans',sans-serif", fontSize: 14, color: "#1A0A2E", background: "#faf7ff", outline: "none" };

  return (
    <div>
      <p style={{ fontSize: 14, color: "#6B7280", marginBottom: 16 }}>Décrivez votre activité en quelques mots :</p>
      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <input style={{ ...inputStyle, flex: 1 }} placeholder="Ex: je fais du conseil en création d'entreprise à distance..."
          value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === "Enter" && search()} />
        <button className="btn-primary" onClick={() => search()} style={{ padding: "12px 24px", fontSize: 14, background: `linear-gradient(135deg,${C1},${C2})`, whiteSpace: "nowrap" }}>
          Analyser ✦
        </button>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
        <span style={{ fontSize: 12, color: "#9B7BB8", lineHeight: "26px" }}>Exemples :</span>
        {EXEMPLES.map(e => (
          <button key={e} onClick={() => { setQuery(e); setTimeout(() => search(e), 0); }} style={{ fontSize: 12, padding: "4px 12px", borderRadius: 20, border: "1px solid rgba(196,0,106,0.2)", background: "rgba(196,0,106,0.05)", color: C1, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>{e}</button>
        ))}
      </div>

      {loading && <div style={{ textAlign: "center", padding: "24px 0", color: "#9B7BB8", fontSize: 14 }}>Analyse en cours…</div>}
      {error && <div style={{ color: "#DC2626", fontSize: 13, padding: "8px 0" }}>{error}</div>}

      {results.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
          <p style={{ fontSize: 13, color: "#9B7BB8", marginBottom: 4 }}>Cliquez sur un code pour le sélectionner :</p>
          {results.map((r, i) => (
            <div key={i} onClick={() => setSelected(r)} style={{ background: selected?.code === r.code ? "rgba(196,0,106,0.05)" : "#faf7ff", border: selected?.code === r.code ? `2px solid ${C1}` : "1.5px solid rgba(107,45,139,0.1)", borderRadius: 12, padding: "16px 20px", cursor: "pointer", transition: "all .2s" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 700, color: C1 }}>{r.code}</span>
                <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 20, background: r.confiance === "haute" ? "rgba(59,109,17,0.1)" : "rgba(133,79,11,0.1)", color: r.confiance === "haute" ? "#3B6D11" : "#854F0B" }}>{r.confiance === "haute" ? "✓ Très pertinent" : "~ Pertinent"}</span>
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#1A0A2E", marginBottom: 4 }}>{r.libelle}</div>
              <div style={{ fontSize: 12, color: "#9B7BB8", marginBottom: 6 }}>{r.section}</div>
              <div style={{ fontSize: 12, color: "#6B7280", fontStyle: "italic" }}>{r.explication}</div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <div style={{ background: "#fff", border: `2px solid ${C1}`, borderRadius: 14, padding: "20px 24px" }}>
          <div style={{ fontSize: 12, color: "#9B7BB8", marginBottom: 4 }}>Code APE sélectionné</div>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 32, fontWeight: 700, color: C1, marginBottom: 4 }}>{selected.code}</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#1A0A2E", marginBottom: 4 }}>{selected.libelle}</div>
          <div style={{ fontSize: 13, color: "#9B7BB8", marginBottom: 16 }}>{selected.section}</div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button className="btn-primary" onClick={copy} style={{ padding: "10px 22px", fontSize: 13, background: `linear-gradient(135deg,${C1},${C2})` }}>Copier le code</button>
            {copied && <span style={{ fontSize: 13, color: "#3B6D11", fontWeight: 600 }}>✓ Copié !</span>}
          </div>
          <div style={{ marginTop: 14, padding: "12px 14px", background: "#faf7ff", borderRadius: 10, fontSize: 12, color: "#6B7280", lineHeight: 1.6 }}>
            ⚠️ Ce code sera inscrit dans vos statuts et déclarations officielles (CFE, URSSAF, INSEE). En cas de doute, rapprochez-vous de votre CFE ou d'un expert-comptable.
          </div>
        </div>
      )}
    </div>
  );
}


export default function App() {
  const [page, setPage] = useState("home");
  const [scrolled, setScrolled] = useState(false);
  const topRef = useRef(null);
  const [selDoc, setSelDoc] = useState(null);
  const [formData, setFormData] = useState({});
  const [genDoc, setGenDoc] = useState("");
  const [loading, setLoading] = useState(false);
  const [contact, setContact] = useState({ nom: "", email: "", tel: "", service: "", message: "" });
  const [rdvDate, setRdvDate] = useState("");
  const [rdvHeure, setRdvHeure] = useState("");
  const [fichiers, setFichiers] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [cms] = useState(getCMS());

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  if (window.location.pathname === "/admin") return <BackOffice />;
  if (window.location.pathname === "/mon-espace") return <EspaceClient />;

  const navigate = (p) => {
    setPage(p); setSelDoc(null); setFormData({}); setGenDoc(""); setLoading(false);
    setTimeout(() => topRef.current?.scrollIntoView({ behavior: "smooth" }), 30);
  };

  const handleGenerate = async () => {
    setLoading(true);
    const fields = selDoc.fields.map(f => `- ${f.label}: ${formData[f.key] || "non renseigné"}`).join("\n");
    const prompt = `Tu es un expert juridique français. Génère un document juridique professionnel et complet de type "${selDoc.label}" en français.\n\nInformations:\n${fields}\n\nGénère le document complet prêt à utiliser. Génère uniquement le document, sans commentaires.`;
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 1000, messages: [{ role: "user", content: prompt }] }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error.message);
      setGenDoc(data.content?.map(b => b.text || "").join("\n") || "Erreur.");
    } catch (e) {
      setGenDoc("⚠️ Le générateur IA n'est pas disponible depuis ce navigateur.\n\nContactez-nous directement :\n📞 0690 96 10 04\n✉️ daniela.cordinel@cd-assist.com\n💬 WhatsApp : wa.me/590690961004");
    }
    setLoading(false);
  };

  const S = {
    container: { maxWidth: 1100, margin: "0 auto" },
    h1: { fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(36px,5vw,56px)", fontWeight: 700, color: "#1A0A2E" },
    h2: { fontFamily: "'Cormorant Garamond', serif", fontSize: 42, fontWeight: 700, color: "#1A0A2E" },
    h3: { fontFamily: "'Cormorant Garamond', serif", fontSize: 21, fontWeight: 700, color: "#1A0A2E" },
    card: { background: "#fff", borderRadius: 16, padding: "28px 24px", boxShadow: "0 4px 20px rgba(107,45,139,0.07)", border: "1.5px solid rgba(196,0,106,0.07)" },
    body: { fontSize: 14, color: "#6B7280", lineHeight: 1.75 },
  };

  const C1 = cms.theme.couleur1;
  const C2 = cms.theme.couleur2;

  // Numéro WhatsApp
  const WHATSAPP_NUMBER = "590690961004"; // Format international Guadeloupe

  return (
    <div ref={topRef} style={{ fontFamily: "'DM Sans',sans-serif", background: "#faf7ff", minHeight: "100vh" }}>
      <style>{css}</style>

      {/* ── BOUTON WHATSAPP FLOTTANT ── */}
      <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=Bonjour%20CD'ASSIST%20CONSULTING%2C%20je%20souhaite%20avoir%20des%20informations.`}
        target="_blank" rel="noopener noreferrer"
        style={{
          position: "fixed", bottom: 28, right: 28, zIndex: 9999,
          width: 60, height: 60, borderRadius: "50%",
          background: "#25D366",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 20px rgba(37,211,102,0.4)",
          transition: "all .3s", textDecoration: "none",
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.1)"; e.currentTarget.style.boxShadow = "0 8px 32px rgba(37,211,102,0.5)"; }}
        onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(37,211,102,0.4)"; }}
      >
        <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000, height: 72, background: scrolled || menuOpen ? "rgba(255,255,255,0.97)" : "transparent", backdropFilter: scrolled ? "blur(16px)" : "none", boxShadow: scrolled ? "0 2px 20px rgba(107,45,139,0.1)" : "none", transition: "all .4s", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => { navigate("home"); setMenuOpen(false); }}>
          <Logo size={60} />
          <div>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 15, fontWeight: 700, color: C2, lineHeight: 1 }}>CD'ASSIST</div>
            <div style={{ fontSize: 9, color: C1, letterSpacing: 2.5, textTransform: "uppercase", fontWeight: 700 }}>CONSULTING</div>
          </div>
        </div>

        {/* Desktop nav */}
        <div className="desktop-nav" style={{ display: "flex", gap: 20, alignItems: "center" }}>
          {NAV_LINKS.map(l => (
            <span key={l.id} onClick={() => navigate(l.id)} style={{ fontSize: 12, fontWeight: 500, cursor: "pointer", color: page === l.id ? C1 : C2, borderBottom: page === l.id ? `2px solid ${C1}` : "2px solid transparent", padding: "4px 0", transition: "all .2s" }}>{l.label}</span>
          ))}
          <button className="btn-primary" onClick={() => navigate("contact")} style={{ padding: "9px 18px", fontSize: 12, background: `linear-gradient(135deg,${C1},${C2})` }}>Prendre RDV</button>
          <button onClick={() => window.location.href = "/mon-espace"} style={{ padding: "9px 14px", fontSize: 12, background: "transparent", border: `2px solid ${C2}`, color: C2, borderRadius: 50, fontFamily: "'DM Sans',sans-serif", fontWeight: 600, cursor: "pointer" }}>👤 Mon espace</button>
        </div>

        {/* Hamburger */}
        <button className="hamburger" onClick={() => setMenuOpen(!menuOpen)} style={{ display: "flex", flexDirection: "column", gap: 5, background: "none", border: "none", cursor: "pointer", padding: 8 }}>
          <span style={{ width: 24, height: 2, background: menuOpen ? C1 : C2, borderRadius: 2, transition: "all .3s", transform: menuOpen ? "rotate(45deg) translate(5px,5px)" : "none" }} />
          <span style={{ width: 24, height: 2, background: menuOpen ? C1 : C2, borderRadius: 2, transition: "all .3s", opacity: menuOpen ? 0 : 1 }} />
          <span style={{ width: 24, height: 2, background: menuOpen ? C1 : C2, borderRadius: 2, transition: "all .3s", transform: menuOpen ? "rotate(-45deg) translate(5px,-5px)" : "none" }} />
        </button>
      </nav>

      {/* Menu mobile */}
      {menuOpen && (
        <div className="mobile-menu-overlay" style={{ position: "fixed", top: 72, left: 0, right: 0, bottom: 0, zIndex: 999, background: "rgba(26,10,46,0.5)" }} onClick={() => setMenuOpen(false)}>
          <div className="mobile-menu" style={{ background: "#fff", padding: "24px", display: "flex", flexDirection: "column", gap: 4, boxShadow: "0 8px 32px rgba(107,45,139,0.2)" }} onClick={e => e.stopPropagation()}>
            {NAV_LINKS.map(l => (
              <button key={l.id} onClick={() => { navigate(l.id); setMenuOpen(false); }} style={{ background: page === l.id ? `linear-gradient(135deg,rgba(196,0,106,0.08),rgba(107,45,139,0.08))` : "none", border: "none", padding: "14px 16px", borderRadius: 12, textAlign: "left", fontSize: 15, fontWeight: page === l.id ? 700 : 500, color: page === l.id ? C1 : "#1A0A2E", cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
                {l.label}
              </button>
            ))}
            <div style={{ borderTop: "1px solid #f3e8ff", marginTop: 8, paddingTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
              <button onClick={() => { navigate("contact"); setMenuOpen(false); }} style={{ background: `linear-gradient(135deg,${C1},${C2})`, color: "#fff", border: "none", padding: "14px", borderRadius: 50, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
                📅 Prendre RDV
              </button>
              <button onClick={() => window.location.href = "/mon-espace"} style={{ background: "transparent", border: `2px solid ${C2}`, color: C2, padding: "13px", borderRadius: 50, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
                👤 Mon espace client
              </button>
            </div>
          </div>
        </div>
      )}

      {page === "home" && (
        <>
          <section style={{ minHeight: "100vh", display: "flex", alignItems: "center", background: "linear-gradient(135deg,#faf7ff 0%,#fff0f7 50%,#f3e8ff 100%)", position: "relative", overflow: "hidden", padding: "110px 32px 60px" }}>
            <div style={{ ...S.container, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60, alignItems: "center", width: "100%" }} className="hero-grid">
              <div className="fade-up">
                <span className="tag">{cms.accueil.tag}</span>
                <h1 style={{ ...S.h1, margin: "20px 0 24px", lineHeight: 1.15 }}>
                  Votre assistante<br />
                  <span style={{ background: `linear-gradient(135deg,${C1},${C2})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>administrative</span><br />
                  de confiance
                </h1>
                <p style={{ fontSize: 17, color: "#6B7280", lineHeight: 1.8, marginBottom: 40, maxWidth: 480 }}>{cms.accueil.description}</p>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <button className="btn-primary" onClick={() => navigate("contact")} style={{ background: `linear-gradient(135deg,${C1},${C2})` }}>{cms.accueil.bouton1}</button>
                  <button className="btn-outline" onClick={() => navigate("documents")} style={{ color: C2, borderColor: C2 }}>{cms.accueil.bouton2}</button>
                </div>
                <div style={{ display: "flex", gap: 36, marginTop: 48 }}>
                  {[[cms.accueil.stat1_valeur, cms.accueil.stat1_label],[cms.accueil.stat2_valeur, cms.accueil.stat2_label],[cms.accueil.stat3_valeur, cms.accueil.stat3_label]].map(([n,l]) => (
                    <div key={l}>
                      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 32, fontWeight: 700, color: C1 }}>{n}</div>
                      <div style={{ fontSize: 12, color: "#9B7BB8", fontWeight: 500 }}>{l}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "center" }} className="hero-card">
                <div className="fade-up" style={{ background: "#fff", borderRadius: 24, padding: 36, boxShadow: "0 24px 80px rgba(107,45,139,0.15)", border: "1.5px solid rgba(196,0,106,0.1)", maxWidth: 340, width: "100%" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
                    <Logo size={100} />
                    <div>
                      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 19, fontWeight: 700, color: C2 }}>CD'ASSIST</div>
                      <div style={{ fontSize: 10, color: C1, letterSpacing: 2, textTransform: "uppercase" }}>CONSULTING</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.7, marginBottom: 20 }}>
                    <strong style={{ color: "#1A0A2E" }}>Cordinel Daniela</strong><br/>{cms.apropos.poste}<br/><span style={{ color: C1 }}>📍 {cms.apropos.localisation}</span>
                  </div>
                  {[["⚖️","Création d'entreprise"],["🧾","Déclaration d'impôts"],["📋","Secrétariat externalisé"],["🌱","Conseil ENR"]].map(([ic,tx]) => (
                    <div key={tx} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #f3e8ff", fontSize: 13, color: "#1A0A2E", fontWeight: 500 }}><span>{ic}</span>{tx}</div>
                  ))}
                  <button className="btn-primary" onClick={() => navigate("contact")} style={{ marginTop: 24, width: "100%", padding: "13px", background: `linear-gradient(135deg,${C1},${C2})` }}>Nous contacter →</button>
                </div>
              </div>
            </div>
          </section>

          <section style={{ padding: "80px 32px", background: "#fff" }}>
            <div style={S.container}>
              <div style={{ textAlign: "center", marginBottom: 52 }}>
                <span className="tag">Pourquoi nous choisir</span>
                <h2 style={{ ...S.h2, marginTop: 16 }}>Nos avantages</h2>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 20 }} className="section-grid">
                {AVANTAGES.map(a => (
                  <div key={a.title} className="card-hover" style={{ ...S.card, background: "#faf7ff" }}>
                    <div style={{ fontSize: 32, marginBottom: 12 }}>{a.icon}</div>
                    <h3 style={S.h3}>{a.title}</h3>
                    <p style={{ ...S.body, marginTop: 8 }}>{a.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section style={{ padding: "80px 32px", background: "linear-gradient(135deg,#faf7ff,#fff0f7)" }}>
            <div style={S.container}>
              <div style={{ textAlign: "center", marginBottom: 52 }}>
                <span className="tag">Ce que je propose</span>
                <h2 style={{ ...S.h2, marginTop: 16, marginBottom: 12 }}>Mes prestations</h2>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 20 }}>
                {cms.prestations.map(p => (
                  <div key={p.id} className="card-hover" style={S.card}>
                    <div style={{ fontSize: 32, marginBottom: 14 }}>{p.icon}</div>
                    <h3 style={S.h3}>{p.titre}</h3>
                    <p style={{ ...S.body, margin: "8px 0 16px" }}>{p.description}</p>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C1 }}>{p.prix}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section style={{ padding: "80px 32px", background: `linear-gradient(135deg,${C2},${C1})`, textAlign: "center" }}>
            <div style={{ maxWidth: 620, margin: "0 auto" }}>
              <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 42, color: "#fff", fontWeight: 700, marginBottom: 16 }}>Prêt à déléguer votre administratif ?</h2>
              <p style={{ fontSize: 16, color: "rgba(255,255,255,0.85)", lineHeight: 1.8, marginBottom: 36 }}>Contactez-moi pour un devis gratuit. Disponible en Guadeloupe et à distance.</p>
              <button onClick={() => navigate("contact")} style={{ background: "#fff", color: C1, border: "none", padding: "16px 40px", borderRadius: 50, fontFamily: "'DM Sans',sans-serif", fontWeight: 700, fontSize: 15, cursor: "pointer" }}>Demander un devis gratuit</button>
            </div>
          </section>
        </>
      )}

      {page === "apropos" && (
        <div style={{ padding: "120px 32px 80px" }}>
          <div style={{ ...S.container, maxWidth: 900 }}>
            <span className="tag">À propos</span>
            <h1 style={{ ...S.h1, margin: "16px 0 40px" }}>{cms.apropos.titre}</h1>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 56 }} className="apropos-grid">
              <div>
                <div style={{ background: "linear-gradient(135deg,#f3e8ff,#fff0f7)", borderRadius: 20, padding: 40, border: "2px solid rgba(196,0,106,0.15)", textAlign: "center", marginBottom: 24 }}>
                  <Logo size={100} />
                  <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 700, color: C2, marginTop: 16 }}>Cordinel Daniela</div>
                  <div style={{ fontSize: 13, color: C1, fontWeight: 600, marginTop: 4 }}>{cms.apropos.poste}</div>
                  <div style={{ fontSize: 13, color: "#6B7280", marginTop: 8 }}>📍 {cms.apropos.localisation}</div>
                </div>
              </div>
              <div>
                <p style={{ fontSize: 15, color: "#374151", lineHeight: 1.9, marginBottom: 20 }}>{cms.apropos.paragraphe1}</p>
                <p style={{ fontSize: 15, color: "#374151", lineHeight: 1.9, marginBottom: 20 }}>{cms.apropos.paragraphe2}</p>
                <p style={{ fontSize: 15, color: "#374151", lineHeight: 1.9, marginBottom: 20 }}>{cms.apropos.paragraphe3}</p>
                <div style={{ background: `linear-gradient(135deg,${C2},${C1})`, borderRadius: 16, padding: 28, color: "#fff" }}>
                  <p style={{ fontSize: 15, lineHeight: 1.8, fontStyle: "italic" }}>« {cms.apropos.citation} »</p>
                  <div style={{ marginTop: 16, fontSize: 14, fontWeight: 700 }}>— Cordinel Daniela</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {page === "prestations" && (
        <div style={{ padding: "120px 32px 80px", background: "linear-gradient(135deg,#faf7ff,#fff0f7)", minHeight: "100vh" }}>
          <div style={S.container}>
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <span className="tag">Ce que je propose</span>
              <h1 style={{ ...S.h1, margin: "16px 0" }}>Mes Prestations</h1>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 24 }} className="section-grid">
              {cms.prestations.map(p => (
                <div key={p.id} className="card-hover" style={{ ...S.card, padding: "32px 26px", cursor: "pointer" }}
                  onClick={() => navigate(p.lien || "boutique")}>
                  <div style={{ fontSize: 36, marginBottom: 16 }}>{p.icon}</div>
                  <h3 style={{ ...S.h3, marginBottom: 10 }}>{p.titre}</h3>
                  <p style={{ ...S.body, marginBottom: 20, lineHeight: 1.8 }}>{p.description}</p>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "inline-block", background: "rgba(196,0,106,0.09)", color: C1, padding: "6px 16px", borderRadius: 20, fontSize: 13, fontWeight: 700 }}>{p.prix}</div>
                    <span style={{ fontSize: 13, color: C2, fontWeight: 600 }}>{p.lien === "contact" ? "Prendre RDV →" : "Voir →"}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {page === "documents" && (
        <div style={{ padding: "120px 32px 80px", minHeight: "100vh", background: "#faf7ff" }}>
          <div style={{ ...S.container, maxWidth: 1000 }}>
            {!selDoc && (
              <>
                <div style={{ textAlign: "center", marginBottom: 52 }}>
                  <span className="tag">Intelligence Artificielle</span>
                  <h1 style={{ ...S.h1, margin: "16px 0" }}>Générateur de Documents</h1>
                  <p style={{ ...S.body, maxWidth: 500, margin: "0 auto" }}>Renseignez vos informations et obtenez un document juridique complet conforme au droit français.</p>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 20 }}>
                  {DOCS.map(d => (
                    <div key={d.id} className="card-hover" style={{ ...S.card, cursor: "pointer" }} onClick={() => { setSelDoc(d); setFormData({}); setGenDoc(""); }}>
                      <div style={{ fontSize: 34, marginBottom: 14 }}>{d.icon}</div>
                      <h3 style={{ ...S.h3, marginBottom: 8 }}>{d.label}</h3>
                      <p style={{ fontSize: 12, color: C1, fontWeight: 600 }}>{d.fields.length} champs requis →</p>
                    </div>
                  ))}
                </div>
              </>
            )}
            {selDoc && !genDoc && !loading && (
              <div style={{ maxWidth: 600, margin: "0 auto" }}>
                <button className="btn-outline" onClick={() => setSelDoc(null)} style={{ marginBottom: 32 }}>← Retour</button>
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
                  <span style={{ fontSize: 40 }}>{selDoc.icon}</span>
                  <h1 style={S.h1}>{selDoc.label}</h1>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 20, marginBottom: 36 }}>
                  {selDoc.fields.map(f => (
                    <div key={f.key}>
                      <label className="field-label">{f.label}</label>
                      <input className="field-input" placeholder={f.placeholder} value={formData[f.key] || ""} onChange={e => setFormData({ ...formData, [f.key]: e.target.value })} />
                    </div>
                  ))}
                </div>
                <button className="btn-primary" onClick={handleGenerate} style={{ fontSize: 15, padding: "16px 40px", background: `linear-gradient(135deg,${C1},${C2})` }}>✦ Générer le document</button>
              </div>
            )}
            {selDoc && (loading || genDoc) && (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, flexWrap: "wrap", gap: 16 }}>
                  <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 32, color: "#1A0A2E" }}>{selDoc.icon} {selDoc.label}</h1>
                  {!loading && (
                    <div style={{ display: "flex", gap: 12 }}>
                      <button className="btn-outline" onClick={() => navigator.clipboard.writeText(genDoc)}>Copier</button>
                      <button className="btn-primary" onClick={() => { const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([genDoc], { type: "text/plain" })); a.download = `${selDoc.label}.txt`; a.click(); }} style={{ background: `linear-gradient(135deg,${C1},${C2})` }}>Télécharger</button>
                    </div>
                  )}
                </div>
                <div style={{ ...S.card, minHeight: 300, padding: "40px 48px" }}>
                  {loading ? (
                    <div style={{ textAlign: "center", padding: "60px 0" }}>
                      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 24, color: C2, marginBottom: 20 }}>Génération en cours…</div>
                      <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
                        {[0,1,2].map(i => <div key={i} style={{ width: 10, height: 10, borderRadius: "50%", background: C1, animation: `pulse 1.2s ${i*0.2}s infinite` }} />)}
                      </div>
                    </div>
                  ) : <pre style={{ whiteSpace: "pre-wrap", fontFamily: "'DM Sans',sans-serif", fontSize: 14, lineHeight: 1.8, color: "#374151" }}>{genDoc}</pre>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {page === "boutique" && <Boutique navigate={navigate} C1={C1} C2={C2} />}

      {page === "outils" && (
        <div style={{ padding: "120px 32px 80px", minHeight: "100vh", background: "#faf7ff" }}>
          <div style={{ ...S.container, maxWidth: 800 }}>
            <div style={{ textAlign: "center", marginBottom: 52 }}>
              <span className="tag">Outils gratuits</span>
              <h1 style={{ ...S.h1, margin: "16px 0 16px" }}>Boîte à outils</h1>
              <p style={{ ...S.body, maxWidth: 500, margin: "0 auto" }}>Des outils pratiques pour vous aider dans vos démarches administratives et la création de votre entreprise.</p>
            </div>

            {/* Outil Code APE */}
            <div style={{ ...S.card, padding: "36px", marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 8 }}>
                <span style={{ fontSize: 36 }}>🔍</span>
                <div>
                  <h2 style={{ ...S.h2, fontSize: 26 }}>Trouver mon code APE</h2>
                  <p style={{ ...S.body, marginTop: 4 }}>L'IA analyse votre activité et suggère les codes APE officiels (INSEE/NAF) correspondants.</p>
                </div>
              </div>

              <div style={{ borderTop: "1px solid #f3e8ff", marginTop: 24, paddingTop: 24 }}>
                <ApeSearch C1={C1} C2={C2} />
              </div>
            </div>

            {/* Autres outils à venir */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              {[
                { icon: "🧮", title: "Simulateur de charges", desc: "Estimez vos charges sociales selon votre statut", badge: "Bientôt" },
                { icon: "📅", title: "Calendrier fiscal", desc: "Ne ratez plus aucune échéance fiscale", badge: "Bientôt" },
                { icon: "📊", title: "Prévisionnel simplifié", desc: "Créez votre prévisionnel sur 3 ans", badge: "Bientôt" },
                { icon: "✅", title: "Checklist création", desc: "Toutes les étapes pour créer votre entreprise", badge: "Bientôt" },
              ].map(t => (
                <div key={t.title} style={{ ...S.card, background: "#faf7ff", opacity: 0.7 }}>
                  <div style={{ fontSize: 28, marginBottom: 10 }}>{t.icon}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <h3 style={{ ...S.h3, fontSize: 17 }}>{t.title}</h3>
                    <span style={{ fontSize: 10, fontWeight: 700, background: "rgba(196,0,106,0.1)", color: C1, padding: "2px 10px", borderRadius: 20 }}>{t.badge}</span>
                  </div>
                  <p style={{ ...S.body, fontSize: 13 }}>{t.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}


      {page === "tarifs" && (
        <div style={{ padding: "120px 32px 80px", background: "linear-gradient(135deg,#faf7ff,#fff0f7)", minHeight: "100vh" }}>
          <div style={{ ...S.container, maxWidth: 1100 }}>
            <div style={{ textAlign: "center", marginBottom: 60 }}>
              <span className="tag">Nos offres</span>
              <h1 style={{ ...S.h1, margin: "16px 0" }}>Tarifs</h1>
              <p style={{ ...S.body, maxWidth: 520, margin: "0 auto" }}>TVA non applicable, art. 293B du CGI. Devis gratuit sans engagement.</p>
            </div>
            <h2 style={{ ...S.h2, fontSize: 28, marginBottom: 20 }}>Forfaits mensuels</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(190px,1fr))", gap: 16, marginBottom: 48 }}>
              {cms.tarifs.forfaits.map(t => (
                <div key={t.id} style={{ ...S.card, padding: "28px 20px", textAlign: "center", position: "relative", border: t.populaire ? `2px solid ${C1}` : "1.5px solid rgba(107,45,139,0.1)" }}>
                  {t.populaire && <div style={{ position: "absolute", top: -13, left: "50%", transform: "translateX(-50%)", background: `linear-gradient(135deg,${C1},${C2})`, color: "#fff", padding: "3px 14px", borderRadius: 20, fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" }}>⭐ POPULAIRE</div>}
                  <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 18, fontWeight: 700, color: "#1A0A2E", marginBottom: 8 }}>{t.nom}</div>
                  <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 40, fontWeight: 700, color: t.populaire ? C1 : C2 }}>{t.prix}€</div>
                  <div style={{ fontSize: 12, color: "#9B7BB8", marginBottom: 16 }}>/mois</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C1, background: "rgba(196,0,106,0.08)", padding: "6px 12px", borderRadius: 20 }}>{t.heures}</div>
                  <button className="btn-primary" onClick={() => navigate("contact")} style={{ marginTop: 20, width: "100%", padding: "10px", fontSize: 13, background: `linear-gradient(135deg,${C1},${C2})` }}>Choisir</button>
                </div>
              ))}
            </div>
            <h2 style={{ ...S.h2, fontSize: 26, marginBottom: 16 }}>À la carte</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 12, marginBottom: 40 }}>
              {cms.tarifs.alacarte.map(t => (
                <div key={t.id} style={{ ...S.card, padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 14, color: "#374151" }}>{t.label}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: C1 }}>{t.prix}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {page === "contact" && (
        <div style={{ padding: "120px 32px 80px", minHeight: "100vh", background: "#faf7ff" }}>
          <div style={{ ...S.container, maxWidth: 960 }}>
            {sent ? (
              <div style={{ textAlign: "center", maxWidth: 480, margin: "60px auto" }}>
                <div style={{ fontSize: 64, marginBottom: 24 }}>✅</div>
                <h2 style={{ ...S.h2, marginBottom: 16 }}>Demande envoyée !</h2>
                <p style={{ ...S.body, fontSize: 16 }}>Merci <strong>{contact.nom}</strong> ! Cordinel Daniela vous recontactera dans les 48h.</p>
                <button className="btn-primary" onClick={() => { setSent(false); setContact({ nom:"",email:"",tel:"",service:"",message:"" }); setRdvDate(""); setRdvHeure(""); setFichiers([]); }} style={{ marginTop: 28, background: `linear-gradient(135deg,${C1},${C2})` }}>Nouvelle demande</button>
              </div>
            ) : (
              <>
                <div style={{ textAlign: "center", marginBottom: 52 }}>
                  <span className="tag">Contact & Réservation</span>
                  <h1 style={{ ...S.h1, margin: "16px 0" }}>Prenez rendez-vous</h1>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }} className="contact-grid">
                  <div style={{ ...S.card, padding: 36 }}>
                    <h2 style={{ ...S.h2, fontSize: 26, marginBottom: 28 }}>Vos informations</h2>
                    {[{ key:"nom", label:"Nom complet *", placeholder:"Ex: Jean Dupont", type:"text" }, { key:"email", label:"Email *", placeholder:"Ex: jean@exemple.com", type:"email" }, { key:"tel", label:"Téléphone", placeholder:"Ex: 0690 12 34 56", type:"tel" }].map(f => (
                      <div key={f.key} style={{ marginBottom: 18 }}>
                        <label className="field-label">{f.label}</label>
                        <input type={f.type} placeholder={f.placeholder} className="field-input" value={contact[f.key]} onChange={e => setContact({ ...contact, [f.key]: e.target.value })} />
                      </div>
                    ))}
                    <div style={{ marginBottom: 18 }}>
                      <label className="field-label">Service souhaité</label>
                      <select className="field-input" value={contact.service} onChange={e => setContact({ ...contact, service: e.target.value })}>
                        <option value="">-- Sélectionnez --</option>
                        {SERVICES_LIST.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>

                    {/* Section spéciale Déclaration d'impôts */}
                    {contact.service === "Déclaration d'impôts 📅" && (
                      <div style={{ background: "linear-gradient(135deg,rgba(196,0,106,0.05),rgba(107,45,139,0.05))", borderRadius: 12, padding: 20, border: "1.5px solid rgba(196,0,106,0.2)", marginBottom: 18 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                          <span style={{ fontSize: 20 }}>🧾</span>
                          <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 18, color: "#1A0A2E" }}>Déclaration d'impôts</h3>
                        </div>

                        {/* Méthode recommandée */}
                        <div style={{ background: "linear-gradient(135deg,#1A0A2E,#6B2D8B)", borderRadius: 10, padding: "14px 16px", marginBottom: 12, color: "#fff" }}>
                          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>✅ Méthode recommandée — Dépôt en ligne (gratuit)</div>
                          <p style={{ fontSize: 12, lineHeight: 1.7, color: "rgba(255,255,255,0.85)", marginBottom: 10 }}>Déposez vos documents directement dans votre espace client sécurisé. Rapide, gratuit et 100% conforme RGPD.</p>
                          <button onClick={() => window.location.href = "/mon-espace"} style={{ background: "#C4006A", color: "#fff", border: "none", padding: "8px 18px", borderRadius: 50, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
                            👤 Déposer mes documents →
                          </button>
                        </div>

                        {/* Avertissement RDV physique */}
                        <div style={{ background: "rgba(245,158,11,0.08)", borderRadius: 10, padding: "12px 14px", border: "1px solid rgba(245,158,11,0.3)", marginBottom: 14 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#92400E", marginBottom: 4 }}>⚠️ Vous préférez un rendez-vous à domicile ?</div>
                          <p style={{ fontSize: 12, color: "#78350F", lineHeight: 1.6 }}>
                            CD'ASSIST CONSULTING n'a plus de bureau physique. Un déplacement à votre domicile sera facturé <strong>25€ supplémentaires</strong> en plus de la prestation. Remplissez le formulaire ci-dessous pour demander un RDV.
                          </p>
                        </div>

                        <p style={{ fontSize: 13, color: "#6B7280", marginBottom: 10, lineHeight: 1.6 }}>
                          Documents à préparer pour votre RDV :
                        </p>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
                          {DOCS_IMPOTS.map(d => (
                            <div key={d} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#374151" }}>
                              <span style={{ color: C1, fontWeight: 700 }}>✓</span>{d}
                            </div>
                          ))}
                        </div>
                        <div style={{ borderTop: "1px solid rgba(196,0,106,0.15)", paddingTop: 14 }}>
                          <label className="field-label">Choisir un créneau RDV à domicile</label>
                          <div style={{ marginBottom: 12 }}>
                            <label className="field-label" style={{ fontSize: 10 }}>Date souhaitée</label>
                            <input type="date" className="field-input" value={rdvDate} onChange={e => setRdvDate(e.target.value)} />
                          </div>
                          <label className="field-label" style={{ fontSize: 10 }}>Heure</label>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
                            {HEURES_IMPOTS.map(h => (
                              <button key={h} onClick={() => setRdvHeure(h)} style={{ padding: "7px 13px", borderRadius: 20, fontSize: 13, fontWeight: 500, cursor: "pointer", background: rdvHeure === h ? `linear-gradient(135deg,${C1},${C2})` : "#fff", color: rdvHeure === h ? "#fff" : C2, border: rdvHeure === h ? "none" : `1.5px solid ${C2}` }}>{h}</button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="field-label">Votre message</label>
                      <textarea className="field-input" rows={4} placeholder="Décrivez votre besoin..." value={contact.message} onChange={e => setContact({ ...contact, message: e.target.value })} style={{ resize: "vertical" }} />
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                    <div style={{ ...S.card, padding: 28 }}>
                      <h2 style={{ ...S.h2, fontSize: 22, marginBottom: 20 }}>📅 Choisir un créneau</h2>
                      <div style={{ marginBottom: 16 }}>
                        <label className="field-label">Date</label>
                        <input type="date" className="field-input" value={rdvDate} onChange={e => {
                          setRdvDate(e.target.value);
                          setRdvHeure("");
                        }} min={new Date().toISOString().split("T")[0]} />
                      </div>
                      {rdvDate && (() => {
                        const dispos = JSON.parse(localStorage.getItem("cdassist_dispos_public_v2") || "{}");
                        const heures = dispos[rdvDate] || [];
                        return heures.length > 0 ? (
                          <>
                            <label className="field-label">Heure</label>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                              {heures.map(h => (
                                <button key={h} onClick={() => setRdvHeure(h)} style={{ padding: "7px 13px", borderRadius: 20, fontSize: 13, fontWeight: 500, cursor: "pointer", background: rdvHeure === h ? `linear-gradient(135deg,${C1},${C2})` : "#faf7ff", color: rdvHeure === h ? "#fff" : C2, border: rdvHeure === h ? "none" : "1.5px solid #e5d5f5" }}>{h}</button>
                              ))}
                            </div>
                          </>
                        ) : (
                          <div style={{ background: "rgba(245,158,11,0.08)", borderRadius: 10, padding: "12px 14px", border: "1px solid rgba(245,158,11,0.3)" }}>
                            <p style={{ fontSize: 13, color: "#92400E" }}>⚠️ Aucun créneau disponible ce jour. Choisissez une autre date.</p>
                          </div>
                        );
                      })()}
                    </div>
                    <div style={{ ...S.card, padding: 28 }}>
                      <h2 style={{ ...S.h2, fontSize: 22, marginBottom: 8 }}>📁 Pièces justificatives</h2>
                      <label style={{ display: "block", border: "2px dashed rgba(196,0,106,0.3)", borderRadius: 12, padding: "20px", textAlign: "center", cursor: "pointer" }}>
                        <div style={{ fontSize: 26, marginBottom: 6 }}>📎</div>
                        <div style={{ fontSize: 13, color: C1, fontWeight: 600 }}>Cliquer pour ajouter des fichiers</div>
                        <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => setFichiers(f => [...f, ...Array.from(e.target.files)])} style={{ display: "none" }} />
                      </label>
                      {fichiers.map((f, i) => <div key={i} style={{ fontSize: 13, color: "#374151", padding: "5px 0" }}>📄 {f.name}</div>)}
                    </div>
                    <button className="btn-primary" onClick={async () => {
                      if (contact.nom && contact.email) {
                        // Sauvegarder dans Firestore si RDV
                        if (rdvDate && rdvHeure) {
                          try {
                            const { db } = await import("./firebase_config");
                            const { addDoc, collection, serverTimestamp } = await import("firebase/firestore");
                            await addDoc(collection(db, "rdvs"), {
                              nom: contact.nom,
                              email: contact.email,
                              tel: contact.tel,
                              service: contact.service,
                              message: contact.message,
                              date: rdvDate,
                              heure: rdvHeure,
                              statut: "En attente",
                              createdAt: serverTimestamp(),
                            });
                          } catch (e) { console.log("RDV:", e); }
                        }
                        setSent(true);
                      }
                    }} style={{ padding: "17px", fontSize: 16, borderRadius: 50, background: `linear-gradient(135deg,${C1},${C2})` }}>✓ Envoyer ma demande</button>
                    <p style={{ fontSize: 11, color: "#9B7BB8", textAlign: "center" }}>Réponse garantie sous 48h · Devis gratuit · Confidentialité assurée</p>
                  </div>
                </div>
                <div style={{ marginTop: 36, display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }} className="contact-info-row">
                  {[["📍","Adresse", cms.contact.adresse],["✉️","Email", cms.contact.email],["📞","Téléphone", cms.contact.telephone1]].map(([ic,lb,vl]) => (
                    <div key={lb} style={{ ...S.card, textAlign: "center", padding: 24 }}>
                      <div style={{ fontSize: 26, marginBottom: 8 }}>{ic}</div>
                      <div style={{ fontSize: 11, color: C1, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 4 }}>{lb}</div>
                      <div style={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>{vl}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {page === "mentions" && (
        <div style={{ padding: "120px 32px 80px", minHeight: "100vh", background: "#faf7ff" }}>
          <div style={{ maxWidth: 800, margin: "0 auto" }}>
            <span className="tag">Informations légales</span>
            <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 42, color: "#1A0A2E", margin: "16px 0 40px" }}>Mentions légales</h1>

            {[
              { titre: "1. Éditeur du site", contenu: [
                "Raison sociale : CD'ASSIST CONSULTING",
                "Forme juridique : SARL",
                "Numéro SIRET : 949 131 775 00015",
                "Code APE : 82.11Z — Services administratifs combinés de bureau",
                "Siège social : Guadeloupe, France",
                "Téléphone : 0690 96 10 04 / 0690 96 50 10",
                "Email : daniela.cordinel@cd-assist.com",
                "Directrice de publication : Cordinel Daniela",
              ]},
              { titre: "2. Hébergement", contenu: [
                "Le site cd-assist.com est hébergé par :",
                "Google Firebase — Firebase Hosting",
                "Google LLC, 1600 Amphitheatre Parkway, Mountain View, CA 94043, USA",
                "Site : firebase.google.com",
              ]},
              { titre: "3. Propriété intellectuelle", contenu: [
                "L'ensemble du contenu de ce site (textes, images, logo, graphismes) est la propriété exclusive de CD'ASSIST CONSULTING.",
                "Toute reproduction, représentation, modification ou exploitation, totale ou partielle, du contenu de ce site est interdite sans autorisation préalable écrite de CD'ASSIST CONSULTING.",
                "Les marques et logos présents sur ce site sont déposés par CD'ASSIST CONSULTING.",
              ]},
              { titre: "4. Protection des données personnelles (RGPD)", contenu: [
                "CD'ASSIST CONSULTING collecte des données personnelles dans le cadre de ses prestations de services.",
                "Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés, vous disposez des droits suivants :",
                "— Droit d'accès à vos données personnelles",
                "— Droit de rectification de vos données",
                "— Droit à l'effacement (droit à l'oubli)",
                "— Droit à la portabilité de vos données",
                "— Droit d'opposition au traitement de vos données",
                "Pour exercer ces droits, contactez-nous : daniela.cordinel@cd-assist.com",
                "Responsable du traitement : Cordinel Daniela",
              ]},
              { titre: "5. Cookies", contenu: [
                "Ce site utilise des cookies techniques nécessaires au bon fonctionnement du site (authentification, préférences).",
                "Aucun cookie publicitaire ou de tracking n'est utilisé sans votre consentement.",
                "Vous pouvez paramétrer votre navigateur pour refuser les cookies.",
              ]},
              { titre: "6. Responsabilité", contenu: [
                "CD'ASSIST CONSULTING s'efforce d'assurer l'exactitude et la mise à jour des informations diffusées sur ce site.",
                "Toutefois, CD'ASSIST CONSULTING ne peut garantir l'exactitude, la précision ou l'exhaustivité des informations mises à disposition sur ce site.",
                "En conséquence, CD'ASSIST CONSULTING décline toute responsabilité pour toute imprécision, inexactitude ou omission portant sur des informations disponibles sur ce site.",
              ]},
              { titre: "7. Droit applicable", contenu: [
                "Le présent site et les présentes mentions légales sont soumis au droit français.",
                "En cas de litige, les tribunaux français seront seuls compétents.",
                "Dernière mise à jour : Avril 2025",
              ]},
            ].map(section => (
              <div key={section.titre} style={{ background: "#fff", borderRadius: 16, padding: "28px 32px", marginBottom: 20, boxShadow: "0 2px 12px rgba(107,45,139,0.06)", border: "1.5px solid rgba(196,0,106,0.07)" }}>
                <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, color: "#1A0A2E", marginBottom: 16 }}>{section.titre}</h2>
                {section.contenu.map((line, i) => (
                  <p key={i} style={{ fontSize: 14, color: "#374151", lineHeight: 1.8, marginBottom: 6 }}>{line}</p>
                ))}
              </div>
            ))}

            <div style={{ background: "linear-gradient(135deg,rgba(196,0,106,0.05),rgba(107,45,139,0.05))", borderRadius: 14, padding: "20px 24px", border: "1.5px solid rgba(196,0,106,0.15)", marginTop: 8 }}>
              <p style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.7 }}>
                📞 Pour toute question : <strong>0690 96 10 04</strong> — ✉️ <strong>daniela.cordinel@cd-assist.com</strong>
              </p>
            </div>
          </div>
        </div>
      )}

      <footer style={{ background: "#1A0A2E", padding: "48px 32px 24px" }}>
        <div style={S.container}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 40, marginBottom: 40 }} className="footer-grid">
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <Logo size={70} />
                <div>
                  <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 17, color: "#fff", fontWeight: 700 }}>CD'ASSIST CONSULTING</div>
                  <div style={{ fontSize: 9, color: C1, letterSpacing: 2.5, textTransform: "uppercase" }}>Guadeloupe</div>
                </div>
              </div>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.45)", lineHeight: 1.7, maxWidth: 280 }}>Cabinet de consulting et d'assistance administrative commerciale.</p>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C1, letterSpacing: 2, textTransform: "uppercase", marginBottom: 16 }}>Services</div>
              {["Création d'entreprise","Secrétariat","Déclaration impôts","Contrats","Conseil ENR"].map(s => <div key={s} style={{ fontSize: 13, color: "rgba(255,255,255,0.45)", marginBottom: 8 }}>{s}</div>)}
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C1, letterSpacing: 2, textTransform: "uppercase", marginBottom: 16 }}>Contact</div>
              {[["📍", cms.contact.adresse],["✉️", cms.contact.email],["📞", cms.contact.telephone1],["🌐", cms.contact.site]].map(([ic,tx]) => <div key={tx} style={{ fontSize: 13, color: "rgba(255,255,255,0.45)", marginBottom: 8 }}>{ic} {tx}</div>)}
            </div>
          </div>
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: 24, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.25)" }}>
              © 2025 CD'ASSIST CONSULTING — Cordinel Daniela · Tous droits réservés · cd-assist.com
              <span style={{ margin: "0 12px" }}>·</span>
              <span onClick={() => navigate("mentions")} style={{ cursor: "pointer", textDecoration: "underline", color: "rgba(255,255,255,0.35)" }}>Mentions légales</span>
            </div>
            <a href="https://www.facebook.com/cdassist" target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: 8, background: "#1877F2", color: "#fff", textDecoration: "none", padding: "8px 16px", borderRadius: 50, fontSize: 12, fontWeight: 700 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              Suivre sur Facebook
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
